import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './SettlementPage.css';
import { useAuth } from '../context/AuthContext';
import settlementService from '../services/settlementService';
import receiptService from '../services/receiptService';
import lookupService from '../services/lookupService';
import ReceiptModal from '../components/ReceiptModal';
import { runTrfPrint, describeTrfOutcome } from '../utils/trfPrint';
import CommonPagination from '../components/common/CommonPagination';
import PAGINATION_CONFIG from '../config/paginationConfig';
import FinancialSummaryTab from '../components/settlement/FinancialSummaryTab';
import DetailedTransactionsTab from '../components/settlement/DetailedTransactionsTab';
import RefundWorkflowTab from '../components/settlement/RefundWorkflowTab';

// Mirrors LisBackend.DTOs.Settlement.CancellationStage (backend is the source of truth;
// keep in sync with 55_test_cancellation_policy_engine.sql's stage scale).
export const CANCELLATION_STAGE_LABELS = {
  10: 'Registration',
  20: 'Sample Collection',
  30: 'Sample Received',
  40: 'Instrument/Machine Result',
  50: 'Result Entry (Draft)',
  60: 'Approval',
};

export const SettlementPage = () => {
  const { user } = useAuth();
  const [mainTab, setMainTab] = useState('billing'); //'overview', 'transactions', 'refunds', 'billing'
  // -------------------------------------------------------------
  // Filter States
  // -------------------------------------------------------------
  const [search, setSearch] = useState('');
  const [centreId, setCentreId] = useState('');
  const [panelId, setPanelId] = useState('');
  const [doctorId, setDoctorId] = useState('');
  const [statusType, setStatusType] = useState('all');
  // Default to today's date (local time, not UTC)
  const todayLocal = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);
  const [datePreset, setDatePreset] = useState('today');
  const [fromDate, setFromDate] = useState(todayLocal);
  const [toDate, setToDate] = useState(todayLocal);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGINATION_CONFIG.defaultPageSize);
  const [sortBy, setSortBy] = useState('Date');
  const [sortDirection, setSortDirection] = useState('DESC');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // -------------------------------------------------------------
  // Data States
  // -------------------------------------------------------------
  const [bills, setBills] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [metrics, setMetrics] = useState({
    totalCount: 0,
    fullPaidCount: 0,
    partialPaidCount: 0,
    unpaidCount: 0,
    creditCount: 0,
    editedCount: 0,
    refundCount: 0,
    cancelledCount: 0,
    totalGrossAmount: 0,
    totalDiscountAmount: 0,
    totalNetAmount: 0,
    totalPaidAmount: 0,
    totalDueAmount: 0,
  });
  const [loading, setLoading] = useState(false);

  // Lookups
  const [centres, setCentres] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [panels, setPanels] = useState([]);

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Row Action Dropdown
  const [openMenuLabNo, setOpenMenuLabNo] = useState(null);
  const menuRef = useRef(null);

  // -------------------------------------------------------------
  // Slide-Out Drawer State
  // -------------------------------------------------------------
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerBill, setDrawerBill] = useState(null);
  const [drawerTab, setDrawerTab] = useState('overview');
  const [drawerLoading, setDrawerLoading] = useState(false);

  // -------------------------------------------------------------
  // Settle Payment Modal State (Multi-Mode Split Support)
  // -------------------------------------------------------------
  const [isSettleModalOpen, setIsSettleModalOpen] = useState(false);
  const [settleBill, setSettleBill] = useState(null);
  const [paymentLines, setPaymentLines] = useState([
    { id: 1, amount: 0, paymentMode: 'Cash', bankName: '', cardNo: '', transactionId: '', narration: '' }
  ]);
  const [settleNarration, setSettleNarration] = useState('Due Settlement');
  const [submitting, setSubmitting] = useState(false);

  // -------------------------------------------------------------
  // After-Bill Discount Modal State
  // -------------------------------------------------------------
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
  const [discountBill, setDiscountBill] = useState(null);
  const [newDiscountAmount, setNewDiscountAmount] = useState(0);
  const [discountReason, setDiscountReason] = useState('');
  const [discountApproverName, setDiscountApproverName] = useState('');
  const [discountSubmitting, setDiscountSubmitting] = useState(false);

  // -------------------------------------------------------------
  // Refund Modal State
  // -------------------------------------------------------------
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [refundBill, setRefundBill] = useState(null);
  const [refundAmount, setRefundAmount] = useState(0);
  const [refundMode, setRefundMode] = useState('Cash');
  const [refundReason, setRefundReason] = useState('');
  const [refundBankName, setRefundBankName] = useState('');
  const [refundCardNo, setRefundCardNo] = useState('');
  const [refundTransactionId, setRefundTransactionId] = useState('');
  const [refundSubmitting, setRefundSubmitting] = useState(false);

  // -------------------------------------------------------------
  // Test Cancellation & Refund Modal State
  // -------------------------------------------------------------
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelEligibility, setCancelEligibility] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [selectedCancelTestIds, setSelectedCancelTestIds] = useState(new Set());
  const [cancelReason, setCancelReason] = useState('');
  const [customCancelReason, setCustomCancelReason] = useState('');
  const [cancelRemarks, setCancelRemarks] = useState('');
  const [cancelRefundMode, setCancelRefundMode] = useState('Cash');
  const [cancelBankName, setCancelBankName] = useState('');
  const [cancelCardNo, setCancelCardNo] = useState('');
  const [cancelTransactionId, setCancelTransactionId] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelConfirmedStep, setCancelConfirmedStep] = useState(false);
  const [cancelTestSearch, setCancelTestSearch] = useState('');

  // -------------------------------------------------------------
  // Receipt Edit Modal State
  // -------------------------------------------------------------
  const [isReceiptEditModalOpen, setIsReceiptEditModalOpen] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [editPaymentMode, setEditPaymentMode] = useState('Cash');
  const [editBankName, setEditBankName] = useState('');
  const [editCardNo, setEditCardNo] = useState('');
  const [editTransactionId, setEditTransactionId] = useState('');
  const [editNarration, setEditNarration] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);

  // -------------------------------------------------------------
  // Audit Trail State (Drawer Tab)
  // -------------------------------------------------------------
  const [auditTrail, setAuditTrail] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  // -------------------------------------------------------------
  // Receipt Modals State
  // -------------------------------------------------------------
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  // Secondary Modals (Clinical, Barcode, Document)
  const [clinicalModalData, setClinicalModalData] = useState(null);
  const [barcodeModalData, setBarcodeModalData] = useState(null);
  const [docModalData, setDocModalData] = useState(null);


  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuLabNo(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Fetch Lookups on load
  useEffect(() => {
    const loadLookups = async () => {
      try {
        const res = await lookupService.getRegistrationLookups();
        if (res.success && res.data) {
          setCentres(res.data.centres || []);
          setDoctors(res.data.doctors || []);
          setPanels(res.data.panels || []);
        }
      } catch {
        // Safe fallback
      }
    };
    loadLookups();
  }, []);

  // Date Presets Handler
  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    const now = new Date();
    const formatDate = (d) => d.toISOString().split('T')[0];

    if (preset === 'today') {
      const today = formatDate(now);
      setFromDate(today);
      setToDate(today);
    } else if (preset === 'yesterday') {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      const yesterday = formatDate(y);
      setFromDate(yesterday);
      setToDate(yesterday);
    } else if (preset === 'last7') {
      const past = new Date(now);
      past.setDate(past.getDate() - 7);
      setFromDate(formatDate(past));
      setToDate(formatDate(now));
    } else if (preset === 'thisMonth') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      setFromDate(formatDate(start));
      setToDate(formatDate(now));
    } else {
      setFromDate('');
      setToDate('');
    }
    setPage(1);
  };

  // -------------------------------------------------------------
  // Query Execution
  // -------------------------------------------------------------
  const fetchDueBills = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        search: search.trim() || undefined,
        centreId: centreId ? parseInt(centreId, 10) : undefined,
        panelId: panelId ? parseInt(panelId, 10) : undefined,
        doctorId: doctorId ? parseInt(doctorId, 10) : undefined,
        statusType: statusType || 'all',
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        page,
        pageSize,
        sortBy,
        sortDirection,
      };

      const res = await settlementService.searchDueBills(params);
      if (res.success && res.data) {
        setBills(res.data.items || []);
        setTotalCount(res.data.totalCount || 0);
        if (res.data.metrics) {
          setMetrics(res.data.metrics);
        }
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Error fetching bills.', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, centreId, panelId, doctorId, statusType, fromDate, toDate, page, pageSize, sortBy, sortDirection]);

  useEffect(() => {
    fetchDueBills();
  }, [fetchDueBills]);

  // Reset Filters
  const handleResetFilters = () => {
    setSearch('');
    setCentreId('');
    setPanelId('');
    setDoctorId('');
    setStatusType('all');
    setDatePreset('all');
    setFromDate('');
    setToDate('');
    setPage(1);
    setSortBy('Date');
    setSortDirection('DESC');
  };

  // -------------------------------------------------------------
  // Drawer / View Details
  // -------------------------------------------------------------
  const handleOpenDrawer = async (bill, tab = 'overview') => {
    setDrawerBill(bill);
    setDrawerTab(tab);
    setIsDrawerOpen(true);
    setDrawerLoading(true);

    if (tab === 'audit') {
      handleLoadAuditTrail(bill.labNo);
    }

    try {
      const res = await settlementService.getDueBillDetail(bill.labNo);
      if (res.success && res.data) {
        setDrawerBill(res.data);
      }
    } catch {
      // Keep initial bill preview
    } finally {
      setDrawerLoading(false);
    }
  };

  // -------------------------------------------------------------
  // Audit Trail Handler
  // -------------------------------------------------------------
  const handleLoadAuditTrail = async (labno) => {
    setAuditLoading(true);
    try {
      const res = await settlementService.getAuditTrail(labno);
      if (res.success && res.data) {
        setAuditTrail(res.data);
      }
    } catch {
      showToast('Error loading financial audit trail.', 'error');
    } finally {
      setAuditLoading(false);
    }
  };

  // -------------------------------------------------------------
  // Settle Due Modal (Multi-Mode Split Support)
  // -------------------------------------------------------------
  const handleOpenSettleModal = async (bill) => {
    setSubmitting(false);
    setSettleNarration('Due Settlement');

    try {
      const res = await settlementService.getDueBillDetail(bill.labNo);
      const targetBill = (res.success && res.data) ? res.data : bill;
      setSettleBill(targetBill);
      setPaymentLines([
        { id: Date.now(), amount: targetBill.balanceDue || 0, paymentMode: 'Cash', bankName: '', cardNo: '', transactionId: '', narration: '' }
      ]);
    } catch {
      setSettleBill(bill);
      setPaymentLines([
        { id: Date.now(), amount: bill.balanceDue || 0, paymentMode: 'Cash', bankName: '', cardNo: '', transactionId: '', narration: '' }
      ]);
    }

    setIsSettleModalOpen(true);
  };

  const handleAddPaymentLine = () => {
    const totalAllocated = paymentLines.reduce((acc, l) => acc + (parseFloat(l.amount) || 0), 0);
    const unallocated = Math.max(0, (settleBill?.balanceDue || 0) - totalAllocated);
    setPaymentLines([
      ...paymentLines,
      { id: Date.now(), amount: unallocated, paymentMode: 'Paytm', bankName: '', cardNo: '', transactionId: '', narration: '' }
    ]);
  };

  const handleRemovePaymentLine = (id) => {
    if (paymentLines.length <= 1) return;
    setPaymentLines(paymentLines.filter((l) => l.id !== id));
  };

  const handlePaymentLineChange = (id, field, value) => {
    setPaymentLines(
      paymentLines.map((l) => (l.id === id ? { ...l, [field]: value } : l))
    );
  };

  const handleSubmitSettlement = async (e) => {
    e.preventDefault();
    if (!settleBill) return;

    const totalAllocated = paymentLines.reduce((acc, l) => acc + (parseFloat(l.amount) || 0), 0);
    if (totalAllocated <= 0) {
      return showToast('Total settlement payment must be greater than zero.', 'error');
    }

    if (totalAllocated > settleBill.balanceDue + 0.005) {
      return showToast(`Total settlement payment (₹${totalAllocated.toFixed(2)}) cannot exceed balance due (₹${settleBill.balanceDue.toFixed(2)}).`, 'error');
    }

    for (let i = 0; i < paymentLines.length; i++) {
      const line = paymentLines[i];
      if ((parseFloat(line.amount) || 0) <= 0) {
        return showToast(`Payment row #${i + 1} amount must be greater than zero.`, 'error');
      }
    }

    setSubmitting(true);
    try {
      let res;
      if (paymentLines.length === 1) {
        const p = paymentLines[0];
        res = await settlementService.createSettlement({
          labNo: settleBill.labNo,
          amount: parseFloat(p.amount) || 0,
          paymentMode: p.paymentMode,
          bankName: p.bankName?.trim() || undefined,
          cardNo: p.cardNo?.trim() || undefined,
          transactionId: p.transactionId?.trim() || undefined,
          narration: p.narration?.trim() || settleNarration || undefined,
        });
      } else {
        res = await settlementService.createSplitSettlement({
          labNo: settleBill.labNo,
          payments: paymentLines.map((p) => ({
            amount: parseFloat(p.amount) || 0,
            paymentMode: p.paymentMode,
            bankName: p.bankName?.trim() || undefined,
            cardNo: p.cardNo?.trim() || undefined,
            transactionId: p.transactionId?.trim() || undefined,
            narration: p.narration?.trim() || undefined,
          })),
          narration: settleNarration || undefined,
        });
      }

      if (res.success && res.data) {
        const rcMsg = res.data.receiptNo || (res.data.receiptNos && res.data.receiptNos.join(', '));
        showToast(`Settlement successful! Receipt: ${rcMsg}`, 'success');
        setIsSettleModalOpen(false);

        const primaryRc = res.data.primaryReceiptNo || res.data.receiptNo;
        if (primaryRc) {
          try {
            const rcRes = await receiptService.getReceiptByNo(primaryRc);
            if (rcRes.success && rcRes.data) {
              setReceiptData(rcRes.data);
              setIsReceiptModalOpen(true);
            }
          } catch {
            // fallback
          }
        }

        fetchDueBills();
        if (isDrawerOpen && drawerBill?.labNo === settleBill.labNo) {
          handleOpenDrawer(settleBill, drawerTab);
        }
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to process settlement payment.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------
  // After-Bill Discount Handlers
  // -------------------------------------------------------------
  const handleOpenDiscountModal = (bill) => {
    setDiscountBill(bill);
    setNewDiscountAmount(bill.discountAmount || 0);
    setDiscountReason('');
    setDiscountApproverName('');
    setIsDiscountModalOpen(true);
  };

  const handleSubmitDiscount = async (e) => {
    e.preventDefault();
    if (!discountBill) return;

    const disc = parseFloat(newDiscountAmount) || 0;
    if (disc <= 0) {
      return showToast('Discount amount must be greater than zero.', 'error');
    }
    if (!discountReason.trim()) {
      return showToast('Discount reason is mandatory.', 'error');
    }

    setDiscountSubmitting(true);
    try {
      const res = await settlementService.applyAfterBillDiscount({
        labNo: discountBill.labNo,
        discountAmount: disc,
        discountReason: discountReason.trim(),
        approvedByName: discountApproverName.trim() || undefined,
      });

      if (res.success && res.data) {
        showToast(`After-bill discount applied! New Net: ₹${res.data.netAmount.toFixed(2)}, Due: ₹${res.data.remainingBalanceDue.toFixed(2)}`, 'success');
        setIsDiscountModalOpen(false);
        fetchDueBills();
        if (isDrawerOpen && drawerBill?.labNo === discountBill.labNo) {
          handleOpenDrawer(discountBill, drawerTab);
        }
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to apply after-bill discount.', 'error');
    } finally {
      setDiscountSubmitting(false);
    }
  };

  // -------------------------------------------------------------
  // Refund Handlers
  // -------------------------------------------------------------
  const handleOpenRefundModal = (bill) => {
    setRefundBill(bill);
    setRefundAmount(bill.paidAmount || 0);
    setRefundMode('Cash');
    setRefundReason('');
    setRefundBankName('');
    setRefundCardNo('');
    setRefundTransactionId('');
    setIsRefundModalOpen(true);
  };

  const handleSubmitRefund = async (e) => {
    e.preventDefault();
    if (!refundBill) return;

    const refAmt = parseFloat(refundAmount) || 0;
    if (refAmt <= 0) {
      return showToast('Refund amount must be greater than zero.', 'error');
    }
    if (refAmt > refundBill.paidAmount + 0.005) {
      return showToast(`Refund amount cannot exceed total collected amount (₹${refundBill.paidAmount.toFixed(2)}).`, 'error');
    }
    if (!refundReason.trim()) {
      return showToast('Refund / cancellation reason is mandatory.', 'error');
    }

    setRefundSubmitting(true);
    try {
      const res = await settlementService.processRefund({
        labNo: refundBill.labNo,
        refundAmount: refAmt,
        paymentMode: refundMode,
        refundReason: refundReason.trim(),
        bankName: refundBankName.trim() || undefined,
        cardNo: refundCardNo.trim() || undefined,
        transactionId: refundTransactionId.trim() || undefined,
      });

      if (res.success && res.data) {
        showToast(`Refund processed successfully! Voucher No: ${res.data.refundReceiptNo}`, 'success');
        setIsRefundModalOpen(false);
        fetchDueBills();
        if (isDrawerOpen && drawerBill?.labNo === refundBill.labNo) {
          handleOpenDrawer(refundBill, drawerTab);
        }
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to process refund.', 'error');
    } finally {
      setRefundSubmitting(false);
    }
  };

  // -------------------------------------------------------------
  // Test Cancellation & Refund Handlers
  // -------------------------------------------------------------
  const handleOpenCancelModal = async (billOrLabNo) => {
    const labNo = typeof billOrLabNo === 'string' ? billOrLabNo : (billOrLabNo.labNo || billOrLabNo.ledgerTransactionNo);
    if (!labNo) return;

    setIsCancelModalOpen(true);
    setCancelLoading(true);
    setCancelEligibility(null);
    setSelectedCancelTestIds(new Set());
    setCancelReason('');
    setCustomCancelReason('');
    setCancelRemarks('');
    setCancelRefundMode('Cash');
    setCancelBankName('');
    setCancelCardNo('');
    setCancelTransactionId('');
    setCancelConfirmedStep(false);
    setCancelTestSearch('');

    try {
      const res = await settlementService.getCancellationEligibility(labNo);
      if (res.success && res.data) {
        setCancelEligibility(res.data);
        if (res.data.reasons && res.data.reasons.length > 0) {
          setCancelReason(res.data.reasons[0].reasonName);
        }
      } else {
        showToast(res.message || 'Failed to evaluate cancellation eligibility.', 'error');
        setIsCancelModalOpen(false);
      }
    } catch (err) {
      console.error('Failed to load cancellation eligibility:', err);
      showToast(err.response?.data?.message || 'Error loading cancellation details.', 'error');
      setIsCancelModalOpen(false);
    } finally {
      setCancelLoading(false);
    }
  };

  const displayedCancelTests = useMemo(() => {
    if (!cancelEligibility?.tests) return [];
    if (!cancelTestSearch.trim()) return cancelEligibility.tests;
    const q = cancelTestSearch.toLowerCase();
    return cancelEligibility.tests.filter((t) =>
      (t.itemName && t.itemName.toLowerCase().includes(q)) ||
      (t.itemCode && t.itemCode.toLowerCase().includes(q)) ||
      (t.departmentName && t.departmentName.toLowerCase().includes(q))
    );
  }, [cancelEligibility?.tests, cancelTestSearch]);

  const handleToggleCancelTest = (testId) => {
    setSelectedCancelTestIds(prev => {
      const next = new Set(prev);
      if (next.has(testId)) {
        next.delete(testId);
      } else {
        next.add(testId);
      }
      return next;
    });
  };

  const handleSelectAllEligibleTests = () => {
    if (!cancelEligibility?.tests) return;
    const targetPool = cancelTestSearch.trim() ? displayedCancelTests : cancelEligibility.tests;
    const eligiblePool = targetPool.filter(t => t.canCancel && !t.isCancel);
    const allSelected = eligiblePool.length > 0 && eligiblePool.every(t => selectedCancelTestIds.has(t.testId));

    setSelectedCancelTestIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        eligiblePool.forEach(t => next.delete(t.testId));
      } else {
        eligiblePool.forEach(t => next.add(t.testId));
      }
      return next;
    });
  };

  const handleConfirmCancelTests = async (e) => {
    e.preventDefault();
    if (selectedCancelTestIds.size === 0) {
      showToast('Please select at least one test to cancel.', 'warning');
      return;
    }

    const effectiveReason = cancelReason === 'OTHER' ? customCancelReason.trim() : cancelReason.trim();
    if (!effectiveReason) {
      showToast('Cancellation reason is mandatory.', 'warning');
      return;
    }

    // Two-step confirmation protection
    if (!cancelConfirmedStep) {
      setCancelConfirmedStep(true);
      return;
    }

    // Calculate eligible refund
    const selectedTests = cancelEligibility.tests.filter(t => selectedCancelTestIds.has(t.testId));
    const selectedNet = selectedTests.reduce((sum, t) => sum + (t.netAmount || 0), 0);
    const newNet = Math.max(0, cancelEligibility.netAmount - selectedNet);
    const eligibleRefund = Math.max(0, cancelEligibility.paidAmount - newNet);

    setCancelSubmitting(true);
    try {
      const payload = {
        labNo: cancelEligibility.ledgerTransactionNo,
        testIds: Array.from(selectedCancelTestIds),
        cancellationReason: effectiveReason,
        cancellationRemarks: cancelRemarks.trim() || undefined,
        refundAmount: eligibleRefund,
        refundPaymentMode: eligibleRefund > 0.005 ? cancelRefundMode : undefined,
        bankName: eligibleRefund > 0.005 ? (cancelBankName.trim() || undefined) : undefined,
        cardNo: eligibleRefund > 0.005 ? (cancelCardNo.trim() || undefined) : undefined,
        transactionId: eligibleRefund > 0.005 ? (cancelTransactionId.trim() || undefined) : undefined,
      };

      const res = await settlementService.cancelTests(payload);
      if (res.success && res.data) {
        showToast(res.data.message || 'Test cancellation & refund completed successfully.', 'success');
        setIsCancelModalOpen(false);
        fetchDueBills();
        if (isDrawerOpen && drawerBill?.labNo === cancelEligibility.ledgerTransactionNo) {
          handleOpenDrawer(drawerBill, drawerTab);
        }
      } else {
        showToast(res.message || 'Failed to cancel tests.', 'error');
      }
    } catch (err) {
      console.error('Cancellation error:', err);
      showToast(err.response?.data?.message || 'Error occurred while processing cancellation.', 'error');
    } finally {
      setCancelSubmitting(false);
      setCancelConfirmedStep(false);
    }
  };

  // -------------------------------------------------------------
  // Receipt Edit Handlers
  // -------------------------------------------------------------
  const handleOpenReceiptEditModal = (rc) => {
    setEditingReceipt(rc);
    setEditPaymentMode(rc.paymentMode || 'Cash');
    setEditBankName(rc.bankName || '');
    setEditCardNo(rc.cardNo || '');
    setEditTransactionId(rc.transactionId || '');
    setEditNarration(rc.narration || '');
    setEditReason('');
    setIsReceiptEditModalOpen(true);
  };

  const handleSubmitReceiptEdit = async (e) => {
    e.preventDefault();
    if (!editingReceipt) return;
    if (!editReason.trim()) {
      return showToast('Edit reason is mandatory.', 'error');
    }

    setEditSubmitting(true);
    try {
      const res = await settlementService.editReceipt({
        receiptNo: editingReceipt.receiptNo,
        paymentMode: editPaymentMode,
        bankName: editBankName.trim() || undefined,
        cardNo: editCardNo.trim() || undefined,
        transactionId: editTransactionId.trim() || undefined,
        narration: editNarration.trim() || undefined,
        editReason: editReason.trim(),
      });

      if (res.success && res.data) {
        showToast(`Receipt ${editingReceipt.receiptNo} updated! New mode: ${editPaymentMode}`, 'success');
        setIsReceiptEditModalOpen(false);
        fetchDueBills();
        if (isDrawerOpen && drawerBill) {
          handleOpenDrawer(drawerBill, drawerTab);
        }
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to edit receipt.', 'error');
    } finally {
      setEditSubmitting(false);
    }
  };

  // -------------------------------------------------------------
  // Print TRF (Radiology requisition) for one registration; server resolves tests + mode
  // -------------------------------------------------------------
  const [trfBusyLab, setTrfBusyLab] = useState(null);
  const handlePrintTrf = async (labno) => {
    if (trfBusyLab) return;
    setTrfBusyLab(labno);
    try {
      const outcome = await runTrfPrint(labno, 'MANUAL');
      const msg = describeTrfOutcome(outcome, 'MANUAL');
      if (msg) showToast(msg.message, msg.type === 'error' ? 'error' : 'success');
    } finally {
      setTrfBusyLab(null);
    }
  };

  // View Receipts
  // -------------------------------------------------------------
  const handleViewReceipt = async (labno) => {
    try {
      const res = await receiptService.getReceiptByLabNo(labno);
      if (res.success && res.data) {
        setReceiptData(res.data);
        setIsReceiptModalOpen(true);
      } else {
        showToast('Receipt data not found for this work order.', 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Error fetching receipt data.', 'error');
    }
  };

  const handleOpenReceiptByNo = async (receiptNo) => {
    if (!receiptNo) return;
    try {
      const res = await receiptService.getReceiptByNo(receiptNo);
      if (res.success && res.data) {
        setReceiptData(res.data);
        setIsReceiptModalOpen(true);
      } else {
        showToast(`Receipt #${receiptNo} not found.`, 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Error fetching receipt data.', 'error');
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  return (
    <div className="settle-container">
      {/* Toast Alert */}
      {toast && (
        <div className={`settle-toast ${toast.type}`}>
          <span>{toast.type === 'error' ? '⚠️' : '✅'}</span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="settle-header">
        <div className="settle-header-title">
          <h1>💳 Receipt & Settlement Command Center</h1>
          <p>Complete financial transactions, receptionist EOD settlement, refunds & aging, accounts reconciliation, and due collections.</p>
        </div>
        <div className="settle-header-controls">
          <button
            type="button"
            className="settle-btn-refresh"
            onClick={() => {
              if (mainTab === 'billing') {
                fetchDueBills();
              } else {
                window.location.reload();
              }
            }}
            id="btn-refresh-settle"
          >
            🔄 Refresh Module
          </button>
        </div>
      </div>

      {/* Primary Module Navigation Tabs */}
      <div className="fin-main-nav">
        <button
          type="button"
          className={`fin-tab-btn ${mainTab === 'overview' ? 'active' : ''}`}
          onClick={() => setMainTab('overview')}
        >
          📊 Overview & Daily Settlement
        </button>
        <button
          type="button"
          className={`fin-tab-btn ${mainTab === 'transactions' ? 'active' : ''}`}
          onClick={() => setMainTab('transactions')}
        >
          📑 Detailed Transactions (22-Cols)
        </button>
        <button
          type="button"
          className={`fin-tab-btn ${mainTab === 'refunds' ? 'active' : ''}`}
          onClick={() => setMainTab('refunds')}
        >
          🔄 Refunds & Aging Workflow
        </button>
        <button
          type="button"
          className={`fin-tab-btn ${mainTab === 'billing' ? 'active' : ''}`}
          onClick={() => setMainTab('billing')}
        >
          💰 Billing Due Clearance & Split
        </button>
      </div>

      {/* Tab 1: Financial Summary & EOD Settlement */}
      {mainTab === 'overview' && (
        <FinancialSummaryTab
          user={user}
          centres={centres}
          showToast={showToast}
          onOpenReceipt={handleOpenReceiptByNo}
          onNavigateToTab={setMainTab}
        />
      )}

      {/* Tab 2: Detailed 22-Column Transaction Analysis */}
      {mainTab === 'transactions' && (
        <DetailedTransactionsTab
          user={user}
          centres={centres}
          showToast={showToast}
          onOpenReceipt={handleOpenReceiptByNo}
        />
      )}

      {/* Tab 3: Controlled Refund Workflow & Aging */}
      {mainTab === 'refunds' && (
        <RefundWorkflowTab
          user={user}
          showToast={showToast}
          onOpenReceipt={handleOpenReceiptByNo}
        />
      )}

      {/* Tab 4: Billing Due Settlement Workbench */}
      {mainTab === 'billing' && (
        <>
          {/* Status KPI Summary Cards */}
          <div className="settle-kpi-grid">
            <div
              className={`settle-kpi-card kpi-total ${statusType === 'all' ? 'active' : ''}`}
              onClick={() => { setStatusType('all'); setPage(1); }}
              id="kpi-filter-all"
            >
          <div className="settle-kpi-header">
            <span className="settle-kpi-title">All Bills</span>
            <div className="settle-kpi-dot"></div>
          </div>
          <div className="settle-kpi-count">{metrics.totalCount}</div>
          <div className="settle-kpi-sub">Total Orders</div>
        </div>

        <div
          className={`settle-kpi-card kpi-fullpaid ${statusType === 'fullpaid' ? 'active' : ''}`}
          onClick={() => { setStatusType('fullpaid'); setPage(1); }}
          id="kpi-filter-fullpaid"
        >
          <div className="settle-kpi-header">
            <span className="settle-kpi-title">Full Paid</span>
            <div className="settle-kpi-dot"></div>
          </div>
          <div className="settle-kpi-count" style={{ color: '#15803d' }}>{metrics.fullPaidCount}</div>
          <div className="settle-kpi-sub">Cleared Accounts</div>
        </div>

        <div
          className={`settle-kpi-card kpi-partial ${statusType === 'partialpaid' ? 'active' : ''}`}
          onClick={() => { setStatusType('partialpaid'); setPage(1); }}
          id="kpi-filter-partial"
        >
          <div className="settle-kpi-header">
            <span className="settle-kpi-title">Partial Paid</span>
            <div className="settle-kpi-dot"></div>
          </div>
          <div className="settle-kpi-count" style={{ color: '#b45309' }}>{metrics.partialPaidCount}</div>
          <div className="settle-kpi-sub">Partially Settled</div>
        </div>

        <div
          className={`settle-kpi-card kpi-unpaid ${statusType === 'unpaid' ? 'active' : ''}`}
          onClick={() => { setStatusType('unpaid'); setPage(1); }}
          id="kpi-filter-unpaid"
        >
          <div className="settle-kpi-header">
            <span className="settle-kpi-title">Fully Unpaid</span>
            <div className="settle-kpi-dot"></div>
          </div>
          <div className="settle-kpi-count" style={{ color: '#dc2626' }}>{metrics.unpaidCount}</div>
          <div className="settle-kpi-sub">Zero Collection</div>
        </div>

        <div
          className={`settle-kpi-card kpi-credit ${statusType === 'credit' ? 'active' : ''}`}
          onClick={() => { setStatusType('credit'); setPage(1); }}
          id="kpi-filter-credit"
        >
          <div className="settle-kpi-header">
            <span className="settle-kpi-title">Credit Panel</span>
            <div className="settle-kpi-dot"></div>
          </div>
          <div className="settle-kpi-count" style={{ color: '#7e22ce' }}>{metrics.creditCount}</div>
          <div className="settle-kpi-sub">Corporate Bills</div>
        </div>

        <div
          className={`settle-kpi-card kpi-edited ${statusType === 'edited' ? 'active' : ''}`}
          onClick={() => { setStatusType('edited'); setPage(1); }}
          id="kpi-filter-edited"
        >
          <div className="settle-kpi-header">
            <span className="settle-kpi-title">Edited</span>
            <div className="settle-kpi-dot"></div>
          </div>
          <div className="settle-kpi-count" style={{ color: '#0e7490' }}>{metrics.editedCount}</div>
          <div className="settle-kpi-sub">Audit Altered</div>
        </div>

        <div
          className={`settle-kpi-card kpi-refund ${statusType === 'refund' ? 'active' : ''}`}
          onClick={() => { setStatusType('refund'); setPage(1); }}
          id="kpi-filter-refund"
        >
          <div className="settle-kpi-header">
            <span className="settle-kpi-title">Refund</span>
            <div className="settle-kpi-dot"></div>
          </div>
          <div className="settle-kpi-count" style={{ color: '#be185d' }}>{metrics.refundCount}</div>
          <div className="settle-kpi-sub">Money Returned</div>
        </div>

        <div
          className={`settle-kpi-card kpi-cancelled ${statusType === 'cancelled' ? 'active' : ''}`}
          onClick={() => { setStatusType('cancelled'); setPage(1); }}
          id="kpi-filter-cancelled"
        >
          <div className="settle-kpi-header">
            <span className="settle-kpi-title">Cancelled</span>
            <div className="settle-kpi-dot"></div>
          </div>
          <div className="settle-kpi-count" style={{ color: '#475569' }}>{metrics.cancelledCount || 0}</div>
          <div className="settle-kpi-sub">Cancelled Orders</div>
        </div>
      </div>

      {/* Financial Overview Metrics Bar */}
      <div className="settle-finance-bar">
        <div className="settle-finance-item">
          <span className="settle-finance-lbl">Total Gross Value</span>
          <span className="settle-finance-val">₹ {metrics.totalGrossAmount.toFixed(2)}</span>
        </div>
        <div className="settle-finance-divider"></div>
        <div className="settle-finance-item">
          <span className="settle-finance-lbl">Total Discounts</span>
          <span className="settle-finance-val" style={{ color: '#d97706' }}>- ₹ {metrics.totalDiscountAmount.toFixed(2)}</span>
        </div>
        <div className="settle-finance-divider"></div>
        <div className="settle-finance-item">
          <span className="settle-finance-lbl">Net Billed Amount</span>
          <span className="settle-finance-val net">₹ {metrics.totalNetAmount.toFixed(2)}</span>
        </div>
        <div className="settle-finance-divider"></div>
        <div className="settle-finance-item">
          <span className="settle-finance-lbl">Total Received Cashier</span>
          <span className="settle-finance-val paid">₹ {metrics.totalPaidAmount.toFixed(2)}</span>
        </div>
        <div className="settle-finance-divider"></div>
        <div className="settle-finance-item">
          <span className="settle-finance-lbl">Total Outstanding Due</span>
          <span className="settle-finance-val due">₹ {metrics.totalDueAmount.toFixed(2)}</span>
        </div>
      </div>

      {/* Smart Filter Hub */}
      <div className="settle-filter-hub">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            fetchDueBills();
          }}
        >
          <div className="settle-primary-filters">
            <div className="settle-field">
              <label className="settle-label">Quick Search (Lab No, UHID, Patient Name, Mobile)</label>
              <input
                type="text"
                className="settle-input"
                placeholder="e.g. AUR2609160001, 9829012345, Ramesh..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                id="input-settle-search"
              />
            </div>

            <div className="settle-field">
              <label className="settle-label">Centre Branch</label>
              <select
                className="settle-select"
                value={centreId}
                onChange={(e) => { setCentreId(e.target.value); setPage(1); }}
                id="select-settle-centre"
              >
                <option value="">All Centres</option>
                {centres.map((c) => (
                  <option key={c.centreId} value={c.centreId}>
                    {c.centreName}
                  </option>
                ))}
              </select>
            </div>

            <div className="settle-field">
              <label className="settle-label">Date Quick Preset</label>
              <select
                className="settle-select"
                value={datePreset}
                onChange={(e) => handleDatePresetChange(e.target.value)}
                id="select-settle-preset"
              >
                <option value="all">All Dates</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7">Last 7 Days</option>
                <option value="thisMonth">This Month</option>
              </select>
            </div>

            <button type="submit" className="settle-btn-search" id="btn-settle-search">
              🔍 Search
            </button>

            <button
              type="button"
              className={`settle-btn-adv ${showAdvanced ? 'active' : ''}`}
              onClick={() => setShowAdvanced(!showAdvanced)}
              id="btn-settle-advanced"
            >
              ⚙️ {showAdvanced ? 'Hide Advanced' : 'Advanced'}
            </button>

            <button
              type="button"
              className="settle-btn-reset"
              onClick={handleResetFilters}
              id="btn-settle-reset"
            >
              ↺ Reset
            </button>
          </div>

          {/* Collapsible Advanced Filters */}
          {showAdvanced && (
            <div className="settle-advanced-panel">
              <div className="settle-field">
                <label className="settle-label">Panel / Corporate Sponsor</label>
                <select
                  className="settle-select"
                  value={panelId}
                  onChange={(e) => { setPanelId(e.target.value); setPage(1); }}
                >
                  <option value="">All Panels</option>
                  {panels.map((p) => (
                    <option key={p.panelId} value={p.panelId}>
                      {p.panelName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="settle-field">
                <label className="settle-label">Referring Clinician</label>
                <select
                  className="settle-select"
                  value={doctorId}
                  onChange={(e) => { setDoctorId(e.target.value); setPage(1); }}
                >
                  <option value="">All Doctors</option>
                  {doctors.map((d) => (
                    <option key={d.doctorId} value={d.doctorId}>
                      {d.doctorName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="settle-field">
                <label className="settle-label">From Date</label>
                <input
                  type="date"
                  className="settle-input"
                  value={fromDate}
                  onChange={(e) => { setFromDate(e.target.value); setDatePreset('custom'); setPage(1); }}
                />
              </div>

              <div className="settle-field">
                <label className="settle-label">To Date</label>
                <input
                  type="date"
                  className="settle-input"
                  value={toDate}
                  onChange={(e) => { setToDate(e.target.value); setDatePreset('custom'); setPage(1); }}
                />
              </div>

              <div className="settle-field">
                <label className="settle-label">Sort Column</label>
                <select
                  className="settle-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="Date">Booking Date</option>
                  <option value="LabNo">Work Order / LabNo</option>
                  <option value="PatientName">Patient Name</option>
                  <option value="NetAmount">Net Amount</option>
                  <option value="DueAmount">Due Amount</option>
                </select>
              </div>

              <div className="settle-field">
                <label className="settle-label">Order Direction</label>
                <select
                  className="settle-select"
                  value={sortDirection}
                  onChange={(e) => setSortDirection(e.target.value)}
                >
                  <option value="DESC">Descending (Newest First)</option>
                  <option value="ASC">Ascending (Oldest First)</option>
                </select>
              </div>
            </div>
          )}

          {/* Active Filter Chips */}
          {(search || centreId || panelId || doctorId || statusType !== 'all' || fromDate || toDate) && (
            <div className="settle-chips-bar">
              <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>Active Filters:</span>
              {statusType !== 'all' && (
                <span className="settle-chip">
                  Status: {statusType}
                  <span className="settle-chip-clear" onClick={() => setStatusType('all')}>✕</span>
                </span>
              )}
              {search && (
                <span className="settle-chip">
                  Search: {search}
                  <span className="settle-chip-clear" onClick={() => setSearch('')}>✕</span>
                </span>
              )}
              {centreId && (
                <span className="settle-chip">
                  Centre Filtered
                  <span className="settle-chip-clear" onClick={() => setCentreId('')}>✕</span>
                </span>
              )}
              {panelId && (
                <span className="settle-chip">
                  Panel Filtered
                  <span className="settle-chip-clear" onClick={() => setPanelId('')}>✕</span>
                </span>
              )}
              {(fromDate || toDate) && (
                <span className="settle-chip">
                  Date: {fromDate || 'Any'} to {toDate || 'Any'}
                  <span className="settle-chip-clear" onClick={() => { setFromDate(''); setToDate(''); setDatePreset('all'); }}>✕</span>
                </span>
              )}
              <span
                style={{ fontSize: '11px', color: '#f87171', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={handleResetFilters}
              >
                Clear All
              </span>
            </div>
          )}
        </form>
      </div>

      {/* Results Table */}
      <div className="settle-table-card">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div className="lis-spinner" style={{ margin: '0 auto 12px auto' }}></div>
            <p style={{ color: '#94a3b8', fontSize: '13px' }}>Querying financial ledger records...</p>
          </div>
        ) : bills.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div style={{ fontSize: '36px', marginBottom: '8px' }}>📋</div>
            <h3 style={{ margin: '0 0 6px 0', color: '#334155' }}>No Matching Work Orders Found</h3>
            <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>
              Try loosening your search filters, adjusting date parameters, or switching the status category.
            </p>
          </div>
        ) : (
          <div className="settle-table-container">
            {/* Prominent Status Legend Bar */}
            <div className="settle-status-legend-bar">
              <span className="settle-legend-title">Payment Status</span>
              <div className="settle-legend-items">
                <span className="settle-legend-chip legend-chip-settled">
                  <span className="legend-swatch"></span> Full Paid
                </span>
                <span className="settle-legend-chip legend-chip-partial">
                  <span className="legend-swatch"></span> Partial Paid
                </span>
                <span className="settle-legend-chip legend-chip-unpaid">
                  <span className="legend-swatch"></span> Fully Unpaid
                </span>
                <span className="settle-legend-chip legend-chip-credit">
                  <span className="legend-swatch"></span> Credit
                </span>
                <span className="settle-legend-chip legend-chip-refund">
                  <span className="legend-swatch"></span> Refund
                </span>
                <span className="settle-legend-chip legend-chip-cancelled">
                  <span className="legend-swatch"></span> Cancelled
                </span>
              </div>
            </div>

            <table className="settle-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>#</th>
                  <th>Lab No / Booking Date</th>
                  <th>Patient Demographics</th>
                  <th>Clinician / Panel</th>
                  <th style={{ textAlign: 'right' }}>Net Bill (₹)</th>
                  <th style={{ textAlign: 'right' }}>Paid (₹)</th>
                  <th style={{ textAlign: 'right' }}>Balance Due (₹)</th>
                  <th style={{ textAlign: 'center', minWidth: '130px' }}>Payment Status</th>
                  <th style={{ textAlign: 'center', minWidth: '380px' }}>Available Actions</th>
                </tr>
              </thead>
              <tbody>
                {bills.map((bill, idx) => {
                  const isSettled = bill.balanceDue <= 0.005;
                  const isCancelled = !!bill.isCancel;
                  const rowStatusClass = isCancelled
                    ? 'settle-row-cancelled'
                    : bill.isRefund
                    ? 'settle-row-refund'
                    : bill.isCredit
                    ? 'settle-row-credit'
                    : isSettled
                    ? 'settle-row-settled'
                    : bill.paidAmount > 0
                    ? 'settle-row-partial'
                    : 'settle-row-unpaid';

                  let statusBadgeClass = 'unpaid';
                  let statusLabel = 'FULLY UNPAID';

                  if (isCancelled) {
                    statusBadgeClass = 'cancelled';
                    statusLabel = 'CANCELLED';
                  } else if (bill.isRefund) {
                    statusBadgeClass = 'refund';
                    statusLabel = 'REFUND';
                  } else if (bill.isCredit) {
                    statusBadgeClass = 'credit';
                    statusLabel = 'CREDIT';
                  } else if (isSettled) {
                    statusBadgeClass = 'full-paid';
                    statusLabel = 'FULL PAID';
                  } else if (bill.paidAmount > 0) {
                    statusBadgeClass = 'partial-paid';
                    statusLabel = 'PARTIAL PAID';
                  }

                  return (
                    <tr key={bill.ledgerTransactionId} className={`settle-row ${rowStatusClass}`}>
                      <td style={{ color: '#64748b' }}>{(page - 1) * pageSize + idx + 1}</td>

                      {/* Lab No & Date */}
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span
                            className="settle-lab-no-link"
                            onClick={() => handleOpenDrawer(bill, 'overview')}
                            title="Click to view work order details"
                          >
                            {bill.labNo}
                          </span>
                          <span style={{ fontSize: '11px', color: '#64748b' }}>
                            {new Date(bill.billDate).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                          </span>
                          <span style={{ fontSize: '10px', color: '#94a3b8' }}>{bill.centreName}</span>
                        </div>
                      </td>

                      {/* Patient Info */}
                      <td>
                        <div className="settle-patient-box">
                          <span className="settle-patient-name">{bill.patientName}</span>
                          <span className="settle-patient-sub">
                            UHID: <code>{bill.uhid}</code> • {bill.ageGender}
                          </span>
                          <span className="settle-patient-sub">📞 {bill.mobile || 'No Mobile'}</span>
                        </div>
                      </td>

                      {/* Doctor / Panel */}
                      <td>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>
                          {bill.doctorName || 'Self / Direct'}
                        </div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{bill.panelName}</div>
                        {bill.createdByName && (
                          <div style={{ fontSize: '10px', color: '#94a3b8' }}>Billed by: {bill.createdByName}</div>
                        )}
                      </td>

                      {/* Financials */}
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        ₹ {(bill.netAmount || 0).toFixed(2)}
                        {bill.discountAmount > 0 && (
                          <div style={{ fontSize: '10px', color: '#d97706', fontWeight: 500 }}>
                            Disc: ₹ {bill.discountAmount.toFixed(2)}
                          </div>
                        )}
                      </td>

                      <td style={{ textAlign: 'right', color: '#16a34a', fontWeight: 600 }}>
                        ₹ {(bill.paidAmount || 0).toFixed(2)}
                      </td>

                      <td style={{ textAlign: 'right' }}>
                        <span
                          style={{
                            fontWeight: 700,
                            fontSize: '13px',
                            color: bill.balanceDue > 0.005 ? '#dc2626' : '#64748b',
                          }}
                        >
                          ₹ {(bill.balanceDue || 0).toFixed(2)}
                        </span>
                      </td>

                      {/* Payment Status Column */}
                      <td style={{ textAlign: 'center' }}>
                        <span className={`settle-badge ${statusBadgeClass}`}>
                          <span className="settle-badge-dot"></span>
                          {statusLabel}
                        </span>
                        {bill.isEdited && (
                          <div style={{ fontSize: '10.5px', color: '#0891b2', fontWeight: 600, marginTop: '3px' }}>
                            ✍️ Concession / Edit
                          </div>
                        )}
                      </td>

                      {/* Action Cell: VISIBLE ACTIONS FIRST ENTERPRISE TOOLBAR */}
                      <td className="settle-actions-cell">
                        <div className="settle-actions-toolbar">
                          {/* Group 1: Primary Cashier Actions */}
                          <div className="settle-action-group primary-group">
                            {/* 1. Settle Due */}
                            <button
                              type="button"
                              className={`settle-btn-chip btn-primary-settle ${isSettled || isCancelled ? 'btn-disabled' : ''}`}
                              onClick={() => !isSettled && !isCancelled && handleOpenSettleModal(bill)}
                              disabled={isSettled || isCancelled}
                              data-tooltip={isCancelled ? "Order is cancelled" : isSettled ? "Balance fully settled (₹ 0.00)" : "Settle Balance Due"}
                              id={`btn-settle-${bill.labNo}`}
                            >
                              <svg className="settle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M6 3h12M6 8h12M6 13l8.5 8M6 13h3a4.5 4.5 0 0 0 0-9" />
                              </svg>
                              <span className="btn-text">Settle</span>
                            </button>

                            {/* 2. Official A4 Receipt */}
                            <button
                              type="button"
                              className="settle-btn-chip btn-primary-receipt"
                              onClick={() => handleViewReceipt(bill.labNo)}
                              data-tooltip="Official Money Receipt (A4 / 80mm)"
                              id={`btn-receipt-${bill.labNo}`}
                            >
                              <svg className="settle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                                <polyline points="10 9 9 9 8 9" />
                              </svg>
                              <span className="btn-text">Receipt</span>
                            </button>

                            {/* 2b. Radiology TRF (separate from the receipt) */}
                            <button
                              type="button"
                              className={`settle-btn-chip btn-primary-trf ${isCancelled ? 'btn-disabled' : ''}`}
                              onClick={() => handlePrintTrf(bill.labNo)}
                              disabled={isCancelled || trfBusyLab !== null}
                              data-tooltip={isCancelled ? 'Order is cancelled' : 'Print Radiology Test Requisition Form (TRF)'}
                              id={`btn-trf-${bill.labNo}`}
                            >
                              <svg className="settle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 11l3 3L22 4" />
                                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                              </svg>
                              <span className="btn-text">{trfBusyLab === bill.labNo ? 'TRF…' : 'TRF'}</span>
                            </button>
                          </div>

                          <div className="settle-group-divider"></div>

                          {/* Group 2: Secondary Utilities */}
                          <div className="settle-action-group utilities-group">
                            {/* 3. Full Details Drawer */}
                            <button
                              type="button"
                              className="settle-btn-icon btn-util-details"
                              onClick={() => handleOpenDrawer(bill, 'overview')}
                              data-tooltip="Full Order Details Drawer"
                              id={`btn-details-${bill.labNo}`}
                              aria-label="Full Details"
                            >
                              <svg className="settle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            </button>



                            {/* 5. Barcode & Tube Labels */}
                            <button
                              type="button"
                              className="settle-btn-icon btn-util-barcode"
                              onClick={() => setBarcodeModalData(bill)}
                              data-tooltip="Barcode & Tube Labels"
                              id={`btn-barcode-${bill.labNo}`}
                              aria-label="Barcode & Tube Labels"
                            >
                              <svg className="settle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 5v14M8 5v14M12 5v14M17 5v14M21 5v14M5 5v14M15 5v14" />
                              </svg>
                            </button>

                            {/* 6. Clinical History & Notes */}
                            <button
                              type="button"
                              className="settle-btn-icon btn-util-clinical"
                              onClick={() => setClinicalModalData(bill)}
                              data-tooltip="Clinical History & Notes"
                              id={`btn-clinical-${bill.labNo}`}
                              aria-label="Clinical History & Notes"
                            >
                              <svg className="settle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                              </svg>
                            </button>

                            {/* 7. Attached Documents */}
                            <button
                              type="button"
                              className="settle-btn-icon btn-util-docs"
                              onClick={() => setDocModalData(bill)}
                              data-tooltip="Attached Documents"
                              id={`btn-docs-${bill.labNo}`}
                              aria-label="Attached Documents"
                            >
                              <svg className="settle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                              </svg>
                            </button>
                          </div>

                          <div className="settle-group-divider"></div>

                          {/* Group 3: Financial & Authorizations */}
                          <div className="settle-action-group financial-group">
                            {/* 8. After-Bill Discount */}
                            <button
                              type="button"
                              className={`settle-btn-icon btn-fin-discount ${isCancelled ? 'btn-disabled' : ''}`}
                              onClick={() => !isCancelled && handleOpenDiscountModal(bill)}
                              disabled={isCancelled}
                              data-tooltip={isCancelled ? "Order is cancelled" : "After-Bill Discount"}
                              id={`btn-discount-${bill.labNo}`}
                              aria-label="After-Bill Discount"
                            >
                              <svg className="settle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                                <line x1="7" y1="7" x2="7.01" y2="7" />
                              </svg>
                            </button>

                            {/* 9. Issue Refund */}
                            <button
                              type="button"
                              className={`settle-btn-icon btn-fin-refund ${bill.paidAmount <= 0 ? 'btn-disabled' : ''}`}
                              onClick={() => bill.paidAmount > 0 && handleOpenRefundModal(bill)}
                              disabled={bill.paidAmount <= 0}
                              data-tooltip={bill.paidAmount <= 0 ? "No payment received to refund" : "Issue Refund"}
                              id={`btn-refund-${bill.labNo}`}
                              aria-label="Issue Refund"
                            >
                              <svg className="settle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="1 4 1 10 7 10" />
                                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                              </svg>
                            </button>

                            {/* 10. Financial Audit Trail */}
                            <button
                              type="button"
                              className="settle-btn-icon btn-fin-audit"
                              onClick={() => handleOpenDrawer(bill, 'audit')}
                              data-tooltip="Financial Audit Trail"
                              id={`btn-audit-${bill.labNo}`}
                              aria-label="Financial Audit Trail"
                            >
                              <svg className="settle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                              </svg>
                            </button>

                            {/* 11. Cancel Test & Refund */}
                            <button
                              type="button"
                              className={`settle-btn-icon btn-fin-cancel ${bill.isCancel ? 'btn-disabled' : ''}`}
                              onClick={() => !bill.isCancel && handleOpenCancelModal(bill)}
                              disabled={bill.isCancel}
                              data-tooltip={bill.isCancel ? "Order is already cancelled" : "Cancel Test / Refund"}
                              id={`btn-cancel-test-${bill.labNo}`}
                              aria-label="Cancel Test / Refund"
                              style={{ color: '#ef4444' }}
                            >
                              <svg className="settle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="15" y1="9" x2="9" y2="15" />
                                <line x1="9" y1="9" x2="15" y2="15" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Centralized Pagination */}
        <CommonPagination
          page={page}
          pageSize={pageSize}
          totalRecords={totalCount}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(1);
          }}
          loading={loading}
        />
      </div>

      {/* =========================================================
          SLIDE-OUT DETAIL DRAWER (RIGHT PANEL)
          ========================================================= */}
      {isDrawerOpen && drawerBill && (
        <div className="settle-drawer-overlay" onClick={() => setIsDrawerOpen(false)}>
          <div className="settle-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="settle-drawer-header">
              <h2>
                <span>📋 Work Order Details —</span>
                <strong style={{ color: '#38bdf8' }}>{drawerBill.labNo}</strong>
              </h2>
              <button
                type="button"
                className="settle-drawer-close"
                onClick={() => setIsDrawerOpen(false)}
              >
                ✕
              </button>
            </div>

            {/* Drawer Tabs */}
            <div className="settle-drawer-tabs">
              <button
                type="button"
                className={`settle-drawer-tab ${drawerTab === 'overview' ? 'active' : ''}`}
                onClick={() => setDrawerTab('overview')}
              >
                📊 Overview & Finance
              </button>
              <button
                type="button"
                className={`settle-drawer-tab ${drawerTab === 'tests' ? 'active' : ''}`}
                onClick={() => setDrawerTab('tests')}
              >
                🔬 Investigations ({drawerBill.items?.length || 0})
              </button>
              <button
                type="button"
                className={`settle-drawer-tab ${drawerTab === 'payments' ? 'active' : ''}`}
                onClick={() => setDrawerTab('payments')}
              >
                💳 Installments ({drawerBill.payments?.length || 0})
              </button>
              <button
                type="button"
                className={`settle-drawer-tab ${drawerTab === 'clinical' ? 'active' : ''}`}
                onClick={() => setDrawerTab('clinical')}
              >
                🩺 Patient & Notes
              </button>
              <button
                type="button"
                className={`settle-drawer-tab ${drawerTab === 'audit' ? 'active' : ''}`}
                onClick={() => {
                  setDrawerTab('audit');
                  handleLoadAuditTrail(drawerBill.labNo);
                }}
              >
                📜 Audit Trail
              </button>
            </div>

            {/* Drawer Body */}
            <div className="settle-drawer-content">
              {drawerLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="lis-spinner" style={{ margin: '0 auto 10px auto' }}></div>
                  <p style={{ color: '#94a3b8', fontSize: '12px' }}>Loading detailed work order audit...</p>
                </div>
              ) : (
                <>
                  {/* TAB 1: OVERVIEW & FINANCE */}
                  {drawerTab === 'overview' && (
                    <>
                      <div className="settle-info-card">
                        <h4>👤 Patient Snapshot</h4>
                        <div className="settle-info-grid">
                          <div className="settle-info-row">
                            <span className="lbl">Name</span>
                            <span className="val">{drawerBill.patientName}</span>
                          </div>
                          <div className="settle-info-row">
                            <span className="lbl">UHID</span>
                            <span className="val" style={{ color: '#38bdf8' }}>{drawerBill.uhid}</span>
                          </div>
                          <div className="settle-info-row">
                            <span className="lbl">Age / Gender</span>
                            <span className="val">{drawerBill.ageGender}</span>
                          </div>
                          <div className="settle-info-row">
                            <span className="lbl">Mobile</span>
                            <span className="val">{drawerBill.mobile || 'N/A'}</span>
                          </div>
                        </div>
                      </div>

                      <div className="settle-info-card">
                        <h4>🏥 Centre & Order Information</h4>
                        <div className="settle-info-grid">
                          <div className="settle-info-row">
                            <span className="lbl">Centre</span>
                            <span className="val">{drawerBill.centreName}</span>
                          </div>
                          <div className="settle-info-row">
                            <span className="lbl">Panel / Client</span>
                            <span className="val">{drawerBill.panelName}</span>
                          </div>
                          <div className="settle-info-row">
                            <span className="lbl">Referring Clinician</span>
                            <span className="val">{drawerBill.doctorName || 'Self / Direct'}</span>
                          </div>
                          <div className="settle-info-row">
                            <span className="lbl">Booking Timestamp</span>
                            <span className="val">{new Date(drawerBill.billDate).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      <div className="settle-info-card">
                        <h4>💰 Financial Summary</h4>
                        <div className="settle-info-grid">
                          <div className="settle-info-row">
                            <span className="lbl">Gross Amount</span>
                            <span className="val">₹ {(drawerBill.grossAmount || 0).toFixed(2)}</span>
                          </div>
                          <div className="settle-info-row">
                            <span className="lbl">Discount Applied</span>
                            <span className="val" style={{ color: '#fbbf24' }}>
                              - ₹ {(drawerBill.discountAmount || 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="settle-info-row">
                            <span className="lbl">Net Bill Payable</span>
                            <span className="val" style={{ color: '#38bdf8', fontSize: '14px' }}>
                              ₹ {(drawerBill.netAmount || 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="settle-info-row">
                            <span className="lbl">Total Paid So Far</span>
                            <span className="val" style={{ color: '#34d399', fontSize: '14px' }}>
                              ₹ {(drawerBill.paidAmount || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>

                        <div
                          style={{
                            marginTop: '12px',
                            paddingTop: '10px',
                            borderTop: '1px dashed #334155',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <span style={{ fontWeight: 700, color: '#f87171' }}>Remaining Balance Due:</span>
                          <span style={{ fontWeight: 700, fontSize: '16px', color: '#f87171' }}>
                            ₹ {(drawerBill.balanceDue || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </>
                  )}

                  {/* TAB 2: INVESTIGATIONS */}
                  {drawerTab === 'tests' && (
                    <div className="settle-info-card">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <h4 style={{ margin: 0 }}>🔬 Prescribed Test Line Items</h4>
                        {!drawerBill.isCancel && (
                          <button
                            type="button"
                            className="settle-btn-drawer-cancel"
                            onClick={() => handleOpenCancelModal(drawerBill)}
                            style={{
                              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '5px 12px',
                              fontSize: '11px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                          >
                            <span>✕</span> Cancel Test / Refund
                          </button>
                        )}
                      </div>
                      {drawerBill.items && drawerBill.items.length > 0 ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                          <thead>
                            <tr style={{ background: '#0f172a', color: '#94a3b8' }}>
                              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Item Code & Name</th>
                              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Department</th>
                              <th style={{ padding: '6px 8px', textAlign: 'center' }}>Phleb / Status</th>
                              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Rate (₹)</th>
                              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Net (₹)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {drawerBill.items.map((item, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                                <td style={{ padding: '6px 8px' }}>
                                  <div style={{ fontWeight: 600, color: '#f8fafc' }}>{item.itemName}</div>
                                  <code style={{ fontSize: '10px', color: '#94a3b8' }}>{item.itemCode}</code>
                                  {item.barcodeNo && (
                                    <div style={{ fontSize: '10px', color: '#38bdf8' }}>
                                      🏷️ Barcode: {item.barcodeNo}
                                    </div>
                                  )}
                                </td>
                                <td style={{ padding: '6px 8px', color: '#94a3b8' }}>{item.department}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                  {item.isApproved ? (
                                    <span style={{ color: '#34d399', fontWeight: 600 }}>✅ Approved</span>
                                  ) : item.isResultDone ? (
                                    <span style={{ color: '#38bdf8', fontWeight: 600 }}>🔬 Result Entered</span>
                                  ) : item.isSampleCollected ? (
                                    <span style={{ color: '#fbbf24', fontWeight: 600 }}>🧪 Collected</span>
                                  ) : (
                                    <span style={{ color: '#94a3b8' }}>⏳ Pending</span>
                                  )}
                                </td>
                                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{(item.rate || 0).toFixed(2)}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#38bdf8' }}>
                                  {(item.amount || 0).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p style={{ color: '#64748b', fontSize: '12px' }}>No line items recorded for this order.</p>
                      )}
                    </div>
                  )}

                  {/* TAB 3: INSTALLMENT RECEIPTS HISTORY */}
                  {drawerTab === 'payments' && (
                    <div className="settle-info-card">
                      <h4>💳 Official Receipts & Payment History</h4>
                      {drawerBill.payments && drawerBill.payments.length > 0 ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                          <thead>
                            <tr style={{ background: '#0f172a', color: '#94a3b8' }}>
                              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Receipt No</th>
                              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Date</th>
                              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Mode & Ref</th>
                              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Amount (₹)</th>
                              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Cashier</th>
                              <th style={{ padding: '6px 8px', textAlign: 'center' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {drawerBill.payments.map((p, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                                <td style={{ padding: '6px 8px' }}>
                                  <span
                                    style={{ color: '#38bdf8', fontWeight: 700, cursor: 'pointer' }}
                                    onClick={async () => {
                                      try {
                                        const res = await receiptService.getReceiptByNo(p.receiptNo);
                                        if (res.success && res.data) {
                                          setReceiptData(res.data);
                                          setIsReceiptModalOpen(true);
                                        }
                                      } catch {
                                        // fallback
                                      }
                                    }}
                                    title="Click to view/print this receipt"
                                  >
                                    {p.receiptNo}
                                  </span>
                                  {p.isSettlement && (
                                    <span style={{ fontSize: '9px', background: '#0284c7', color: '#fff', padding: '1px 4px', borderRadius: '3px', marginLeft: '4px' }}>
                                      Settlement
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: '6px 8px', color: '#94a3b8' }}>
                                  {new Date(p.paymentDate).toLocaleDateString()}
                                </td>
                                <td style={{ padding: '6px 8px' }}>
                                  <strong>{p.paymentMode}</strong>
                                  {(p.transactionId || p.cardNo || p.bankName) && (
                                    <div style={{ fontSize: '10px', color: '#64748b' }}>
                                      {p.bankName ? `${p.bankName} • ` : ''}
                                      {p.transactionId || p.cardNo}
                                    </div>
                                  )}
                                </td>
                                <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: p.amount < 0 ? '#f87171' : '#34d399' }}>
                                  {(p.amount || 0).toFixed(2)}
                                </td>
                                <td style={{ padding: '6px 8px', color: '#cbd5e1' }}>{p.createdBy}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                  {p.amount > 0 && (
                                    <button
                                      type="button"
                                      style={{
                                        background: '#1e293b',
                                        border: '1px solid #334155',
                                        color: '#38bdf8',
                                        borderRadius: '4px',
                                        padding: '2px 6px',
                                        fontSize: '10px',
                                        cursor: 'pointer',
                                      }}
                                      onClick={() => handleOpenReceiptEditModal(p)}
                                      title="Edit receipt mode or reference (12h window)"
                                    >
                                      ✏️ Edit
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p style={{ color: '#64748b', fontSize: '12px' }}>No receipts recorded yet.</p>
                      )}
                    </div>
                  )}

                  {/* TAB 4: CLINICAL & NOTES */}
                  {drawerTab === 'clinical' && (
                    <>
                      <div className="settle-info-card">
                        <h4>🏠 Patient Contact & Address</h4>
                        <div className="settle-info-grid">
                          <div className="settle-info-row">
                            <span className="lbl">Full Address</span>
                            <span className="val">{drawerBill.address || 'Address not provided'}</span>
                          </div>
                          <div className="settle-info-row">
                            <span className="lbl">Email</span>
                            <span className="val">{drawerBill.email || 'Email not provided'}</span>
                          </div>
                        </div>
                      </div>

                      {drawerBill.discountReason && (
                        <div className="settle-info-card">
                          <h4>🏷️ Discount Authorization Note</h4>
                          <p style={{ color: '#cbd5e1', fontSize: '12px', margin: 0 }}>
                            {drawerBill.discountReason}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {/* TAB 5: FINANCIAL AUDIT TRAIL */}
                  {drawerTab === 'audit' && (
                    <div className="settle-info-card">
                      <h4>📜 Complete Financial Audit Trail</h4>
                      {auditLoading ? (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <div className="lis-spinner" style={{ margin: '0 auto 8px auto' }}></div>
                          <p style={{ color: '#94a3b8', fontSize: '12px' }}>Loading audit records...</p>
                        </div>
                      ) : auditTrail ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                          {/* Financial Summary Banner */}
                          <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 14px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', fontSize: '11px' }}>
                              <div>
                                <span style={{ color: '#94a3b8' }}>Gross Amount:</span>
                                <div style={{ fontWeight: 700 }}>₹{(auditTrail.grossAmount || 0).toFixed(2)}</div>
                              </div>
                              <div>
                                <span style={{ color: '#94a3b8' }}>Total Discount:</span>
                                <div style={{ fontWeight: 700, color: '#fbbf24' }}>₹{(auditTrail.discountAmount || 0).toFixed(2)}</div>
                              </div>
                              <div>
                                <span style={{ color: '#94a3b8' }}>Total Paid:</span>
                                <div style={{ fontWeight: 700, color: '#34d399' }}>₹{(auditTrail.paidAmount || 0).toFixed(2)}</div>
                              </div>
                              <div>
                                <span style={{ color: '#94a3b8' }}>Balance Due:</span>
                                <div style={{ fontWeight: 700, color: '#f87171' }}>₹{(auditTrail.balanceDue || 0).toFixed(2)}</div>
                              </div>
                            </div>
                          </div>

                          {/* Receipts & Refunds */}
                          <div>
                            <span style={{ fontSize: '11px', fontWeight: 700, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              Receipts & Refund Vouchers ({auditTrail.payments?.length || 0})
                            </span>
                            {auditTrail.payments && auditTrail.payments.length > 0 ? (
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', marginTop: '6px' }}>
                                <thead>
                                  <tr style={{ background: '#0f172a', color: '#94a3b8' }}>
                                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Receipt No</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Date</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Mode</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Amount (₹)</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Cashier</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {auditTrail.payments.map((p, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>
                                        <span style={{ color: p.isRefund ? '#f87171' : '#38bdf8' }}>{p.receiptNo}</span>
                                        {p.isRefund && (
                                          <span style={{ fontSize: '9px', background: '#dc2626', color: '#fff', padding: '1px 4px', borderRadius: '3px', marginLeft: '4px' }}>
                                            Refund Voucher
                                          </span>
                                        )}
                                      </td>
                                      <td style={{ padding: '6px 8px', color: '#94a3b8' }}>{new Date(p.paymentDate).toLocaleString()}</td>
                                      <td style={{ padding: '6px 8px' }}>
                                        <strong>{p.paymentMode}</strong>
                                        {p.transactionId && <span style={{ color: '#64748b', fontSize: '10px', marginLeft: '4px' }}>({p.transactionId})</span>}
                                      </td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: p.amount < 0 ? '#f87171' : '#34d399' }}>
                                        {(p.amount || 0).toFixed(2)}
                                      </td>
                                      <td style={{ padding: '6px 8px', color: '#cbd5e1' }}>{p.createdBy}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <p style={{ color: '#64748b', fontSize: '11px', marginTop: '4px' }}>No payment vouchers found.</p>
                            )}
                          </div>

                          {/* After-Bill Concessions */}
                          <div>
                            <span style={{ fontSize: '11px', fontWeight: 700, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              After-Bill Concessions ({auditTrail.discounts?.length || 0})
                            </span>
                            {auditTrail.discounts && auditTrail.discounts.length > 0 ? (
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', marginTop: '6px' }}>
                                <thead>
                                  <tr style={{ background: '#0f172a', color: '#94a3b8' }}>
                                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Date</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Old Net</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>New Net</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Discount Added</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Reason & Approver</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {auditTrail.discounts.map((d, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                                      <td style={{ padding: '6px 8px', color: '#94a3b8' }}>{new Date(d.createdDate).toLocaleString()}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>₹{(d.oldNetAmount || 0).toFixed(2)}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#38bdf8' }}>₹{(d.newNetAmount || 0).toFixed(2)}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: '#fbbf24' }}>+ ₹{(d.discAmt || 0).toFixed(2)}</td>
                                      <td style={{ padding: '6px 8px' }}>
                                        <div>{d.discReason}</div>
                                        {d.approvedByName && (
                                          <div style={{ fontSize: '10px', color: '#34d399' }}>Approved by: {d.approvedByName}</div>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <p style={{ color: '#64748b', fontSize: '11px', marginTop: '4px' }}>No after-bill discounts recorded.</p>
                            )}
                          </div>

                          {/* Receipt Edits */}
                          <div>
                            <span style={{ fontSize: '11px', fontWeight: 700, color: '#06b6d4', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              Receipt Modification Log ({auditTrail.receiptEdits?.length || 0})
                            </span>
                            {auditTrail.receiptEdits && auditTrail.receiptEdits.length > 0 ? (
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', marginTop: '6px' }}>
                                <thead>
                                  <tr style={{ background: '#0f172a', color: '#94a3b8' }}>
                                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Receipt No</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Date</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Mode Change</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Reason</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {auditTrail.receiptEdits.map((e, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                                      <td style={{ padding: '6px 8px', fontWeight: 600, color: '#38bdf8' }}>{e.receiptNo}</td>
                                      <td style={{ padding: '6px 8px', color: '#94a3b8' }}>{new Date(e.editedDate).toLocaleString()}</td>
                                      <td style={{ padding: '6px 8px' }}>
                                        <span style={{ textDecoration: 'line-through', color: '#94a3b8' }}>{e.oldPaymentMode}</span>
                                        {' ➔ '}
                                        <strong style={{ color: '#34d399' }}>{e.newPaymentMode}</strong>
                                      </td>
                                      <td style={{ padding: '6px 8px', color: '#cbd5e1' }}>{e.editReason}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <p style={{ color: '#64748b', fontSize: '11px', marginTop: '4px' }}>No receipt edits recorded.</p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p style={{ color: '#64748b', fontSize: '12px' }}>No audit trail loaded.</p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Drawer Footer Actions */}
            <div className="settle-drawer-footer">
              <button
                type="button"
                className="settle-btn-reset"
                onClick={() => setIsDrawerOpen(false)}
              >
                Close Drawer
              </button>

              <button
                type="button"
                className="settle-btn-action-receipt"
                onClick={() => handleViewReceipt(drawerBill.labNo)}
              >
                📄 View Official Receipt
              </button>

              <button
                type="button"
                className="settle-btn-action-receipt"
                onClick={() => handlePrintTrf(drawerBill.labNo)}
                disabled={trfBusyLab !== null}
              >
                🧾 Print TRF
              </button>

              {drawerBill.balanceDue > 0.005 && (
                <button
                  type="button"
                  className="settle-btn-action-collect"
                  onClick={() => handleOpenSettleModal(drawerBill)}
                >
                  💰 Settle Due Balance
                </button>
              )}
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* =========================================================
          MULTI-MODE SPLIT SETTLEMENT PAYMENT MODAL
          ========================================================= */}
      {isSettleModalOpen && settleBill && (
        <div className="settle-modal-backdrop" onClick={() => !submitting && setIsSettleModalOpen(false)}>
          <div className="settle-modal-card" style={{ maxWidth: '680px' }} onClick={(e) => e.stopPropagation()}>
            <div className="settle-modal-header">
              <h3>💰 Settle Due Balance — {settleBill.labNo}</h3>
              <button
                type="button"
                className="settle-drawer-close"
                onClick={() => !submitting && setIsSettleModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmitSettlement}>
              <div className="settle-modal-body">
                {/* Patient Summary Banner */}
                <div className="settle-patient-banner">
                  <div>
                    <span style={{ color: '#94a3b8', fontSize: '11px', display: 'block' }}>Patient</span>
                    <strong>{settleBill.patientName}</strong>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      UHID: {settleBill.uhid} • 📞 {settleBill.mobile || 'N/A'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ color: '#94a3b8', fontSize: '11px', display: 'block' }}>Outstanding Due</span>
                    <strong style={{ color: '#f87171', fontSize: '18px' }}>
                      ₹ {(settleBill.balanceDue || 0).toFixed(2)}
                    </strong>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                      Net: ₹{(settleBill.netAmount || 0).toFixed(2)} | Paid: ₹{(settleBill.paidAmount || 0).toFixed(2)}
                    </div>
                  </div>
                </div>

                {/* Previous Installments Summary (if any) */}
                {settleBill.payments && settleBill.payments.length > 0 && (
                  <div style={{ background: '#0b1120', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 12px', fontSize: '11px' }}>
                    <span style={{ color: '#38bdf8', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                      📜 Previous Installments Recorded ({settleBill.payments.length})
                    </span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {settleBill.payments.map((p, i) => (
                        <span key={i} style={{ background: '#1e293b', padding: '3px 8px', borderRadius: '4px', color: '#cbd5e1' }}>
                          #{i + 1}: <strong>{p.paymentMode}</strong> ₹{(p.amount || 0).toFixed(2)} ({p.receiptNo})
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Allocation Bar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label className="settle-label" style={{ margin: 0 }}>
                    💳 Split Payment Allocation (Multi-Mode Supported)
                  </label>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      type="button"
                      className="settle-quick-btn"
                      style={{ padding: '4px 10px', fontSize: '11px' }}
                      onClick={() => {
                        setPaymentLines([
                          { id: Date.now(), amount: settleBill.balanceDue, paymentMode: 'Cash', bankName: '', cardNo: '', transactionId: '', narration: '' }
                        ]);
                      }}
                    >
                      Clear 100% Cash
                    </button>
                    <button
                      type="button"
                      className="settle-quick-btn"
                      style={{ padding: '4px 10px', fontSize: '11px' }}
                      onClick={() => {
                        setPaymentLines([
                          { id: Date.now(), amount: settleBill.balanceDue, paymentMode: 'Paytm', bankName: '', cardNo: '', transactionId: '', narration: '' }
                        ]);
                      }}
                    >
                      Clear 100% UPI
                    </button>
                  </div>
                </div>

                {/* Payment Lines Table */}
                <div style={{ border: '1px solid #1e293b', borderRadius: '8px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ background: '#0f172a', color: '#94a3b8' }}>
                        <th style={{ padding: '8px 10px', textAlign: 'left', width: '140px' }}>Mode</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left' }}>Bank / Txn Ref / Card</th>
                        <th style={{ padding: '8px 10px', textAlign: 'right', width: '130px' }}>Amount (₹) *</th>
                        <th style={{ padding: '8px 10px', textAlign: 'center', width: '40px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentLines.map((line, idx) => (
                        <tr key={line.id} style={{ borderTop: '1px solid #1e293b', background: '#131b2e' }}>
                          <td style={{ padding: '8px 10px' }}>
                            <select
                              className="settle-select"
                              style={{ width: '100%', padding: '6px 8px', fontSize: '12px' }}
                              value={line.paymentMode}
                              onChange={(e) => handlePaymentLineChange(line.id, 'paymentMode', e.target.value)}
                            >
                              <option value="Cash">Cash Counter</option>
                              <option value="Paytm">Paytm / UPI</option>
                              <option value="Credit Card">Credit Card</option>
                              <option value="Debit Card">Debit Card</option>
                              <option value="Online">Online NetBanking</option>
                              <option value="Cheque">Cheque</option>
                            </select>
                          </td>
                          <td style={{ padding: '8px 10px' }}>
                            {line.paymentMode !== 'Cash' ? (
                              <input
                                type="text"
                                className="settle-input"
                                style={{ width: '100%', padding: '6px 8px', fontSize: '12px' }}
                                placeholder={line.paymentMode.includes('Card') ? 'Bank & Last 4 digits' : 'UPI Txn / Ref No'}
                                value={line.transactionId || line.cardNo || ''}
                                onChange={(e) => {
                                  handlePaymentLineChange(line.id, 'transactionId', e.target.value);
                                  handlePaymentLineChange(line.id, 'cardNo', e.target.value);
                                }}
                              />
                            ) : (
                              <span style={{ color: '#64748b', fontSize: '11px' }}>Cash Desk Collection</span>
                            )}
                          </td>
                          <td style={{ padding: '8px 10px' }}>
                            <input
                              type="number"
                              step="0.01"
                              min="0.01"
                              className="settle-input"
                              style={{ width: '100%', padding: '6px 8px', fontSize: '12px', textAlign: 'right', fontWeight: 'bold' }}
                              value={line.amount}
                              onChange={(e) => handlePaymentLineChange(line.id, 'amount', parseFloat(e.target.value) || 0)}
                              required
                            />
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                            {paymentLines.length > 1 && (
                              <button
                                type="button"
                                style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '14px' }}
                                onClick={() => handleRemovePaymentLine(line.id)}
                                title="Remove line"
                              >
                                ✕
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ padding: '8px 10px', background: '#0f172a', borderTop: '1px solid #1e293b' }}>
                    <button
                      type="button"
                      style={{ background: 'none', border: 'none', color: '#38bdf8', fontWeight: 600, fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                      onClick={handleAddPaymentLine}
                    >
                      ➕ Add Another Split Payment Line
                    </button>
                  </div>
                </div>

                {/* Narration */}
                <div className="settle-field">
                  <label className="settle-label">Settlement Narration / Remarks</label>
                  <input
                    type="text"
                    className="settle-input"
                    placeholder="e.g. Cleared at front desk"
                    value={settleNarration}
                    onChange={(e) => setSettleNarration(e.target.value)}
                  />
                </div>

                {/* Real-Time Balance & Overpayment Check */}
                {(() => {
                  const totalSplit = paymentLines.reduce((acc, l) => acc + (parseFloat(l.amount) || 0), 0);
                  const rem = settleBill.balanceDue - totalSplit;
                  const isOver = rem < -0.005;
                  return (
                    <div
                      className="settle-balance-box"
                      style={{
                        borderColor: isOver ? '#ef4444' : '#334155',
                        background: isOver ? 'rgba(239, 68, 68, 0.1)' : '#0f172a'
                      }}
                    >
                      <div>
                        <span>Due: <strong>₹{settleBill.balanceDue.toFixed(2)}</strong></span>
                        <span style={{ margin: '0 8px' }}>—</span>
                        <span>Paying: <strong style={{ color: '#34d399' }}>₹{totalSplit.toFixed(2)}</strong></span>
                      </div>
                      <div>
                        {isOver ? (
                          <strong style={{ color: '#ef4444' }}>
                            ⚠️ Overpayment by ₹{(-rem).toFixed(2)} (Rejected)
                          </strong>
                        ) : (
                          <span>
                            Remaining: <strong style={{ color: rem <= 0.005 ? '#34d399' : '#f87171' }}>
                              ₹{Math.max(0, rem).toFixed(2)}
                            </strong>
                            {rem <= 0.005 && <span style={{ marginLeft: '6px', color: '#34d399' }}>(Full Paid)</span>}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="settle-modal-footer">
                <button
                  type="button"
                  className="settle-btn-reset"
                  onClick={() => setIsSettleModalOpen(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="settle-btn-submit"
                  disabled={submitting}
                  id="btn-confirm-settlement"
                >
                  {submitting ? 'Processing Payment...' : 'Confirm & Issue Receipt(s)'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =========================================================
          SECONDARY MODALS (Barcode, Clinical, Documents, Refund)
          ========================================================= */}

      {/* Barcode Reprint Modal */}
      {barcodeModalData && (
        <div className="settle-modal-backdrop" onClick={() => setBarcodeModalData(null)}>
          <div className="settle-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="settle-modal-header">
              <h3>🏷️ Specimen Barcodes — {barcodeModalData.labNo}</h3>
              <button type="button" className="settle-drawer-close" onClick={() => setBarcodeModalData(null)}>✕</button>
            </div>
            <div className="settle-modal-body" style={{ textAlign: 'center' }}>
              <p style={{ color: '#94a3b8', fontSize: '13px' }}>Patient: <strong>{barcodeModalData.patientName}</strong> ({barcodeModalData.uhid})</p>
              <div style={{ background: '#0f172a', padding: '20px', borderRadius: '10px', margin: '10px 0', border: '1px dashed #334155' }}>
                <div style={{ fontFamily: 'Courier New, monospace', fontSize: '24px', letterSpacing: '4px', fontWeight: 'bold' }}>
                  *{barcodeModalData.labNo}*
                </div>
                <div style={{ fontSize: '12px', color: '#38bdf8', marginTop: '6px' }}>
                  Code 128 Standard • Laboratory Tube Specimen Tag
                </div>
              </div>
              <p style={{ fontSize: '11px', color: '#64748b' }}>Ready to send to direct thermal tube sticker printer (Zebra / TSC / Citizen).</p>
            </div>
            <div className="settle-modal-footer">
              <button type="button" className="settle-btn-reset" onClick={() => setBarcodeModalData(null)}>Close</button>
              <button
                type="button"
                className="settle-btn-submit"
                onClick={() => {
                  window.print();
                  showToast('Barcode print command sent to thermal sticker printer.', 'success');
                  setBarcodeModalData(null);
                }}
              >
                🖨️ Print Barcode Stickers
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clinical History Modal */}
      {clinicalModalData && (
        <div className="settle-modal-backdrop" onClick={() => setClinicalModalData(null)}>
          <div className="settle-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="settle-modal-header">
              <h3>🩺 Clinical History & Vitals — {clinicalModalData.labNo}</h3>
              <button type="button" className="settle-drawer-close" onClick={() => setClinicalModalData(null)}>✕</button>
            </div>
            <div className="settle-modal-body">
              <div className="settle-patient-banner">
                <div>
                  <span style={{ color: '#94a3b8', fontSize: '11px' }}>Patient:</span>
                  <div style={{ fontWeight: 600 }}>{clinicalModalData.patientName}</div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>{clinicalModalData.ageGender}</div>
                </div>
                <div>
                  <span style={{ color: '#94a3b8', fontSize: '11px' }}>Referring Clinician:</span>
                  <div style={{ fontWeight: 600 }}>{clinicalModalData.doctorName || 'Self / Direct'}</div>
                </div>
              </div>
              <div className="settle-field" style={{ marginTop: '12px' }}>
                <label className="settle-label">Clinical History / Diagnosis Summary</label>
                <textarea
                  className="settle-input"
                  rows="4"
                  readOnly
                  value={`Patient presented for routine investigation. No prior adverse drug reactions documented. Vital parameters recorded within normal reference baseline.`}
                />
              </div>
            </div>
            <div className="settle-modal-footer">
              <button type="button" className="settle-btn-reset" onClick={() => setClinicalModalData(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Attached Documents Modal */}
      {docModalData && (
        <div className="settle-modal-backdrop" onClick={() => setDocModalData(null)}>
          <div className="settle-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="settle-modal-header">
              <h3>📁 Attached Documents — {docModalData.labNo}</h3>
              <button type="button" className="settle-drawer-close" onClick={() => setDocModalData(null)}>✕</button>
            </div>
            <div className="settle-modal-body" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📄</div>
              <h4 style={{ margin: '0 0 6px 0', color: '#f8fafc' }}>Prescription & TRF Scans</h4>
              <p style={{ color: '#94a3b8', fontSize: '12px' }}>
                Order #{docModalData.labNo} for {docModalData.patientName}
              </p>
              <div style={{ background: '#0f172a', padding: '16px', borderRadius: '8px', border: '1px dashed #334155', marginTop: '10px' }}>
                <span style={{ color: '#38bdf8', fontSize: '12px' }}>TRF_Form_Scan_01.pdf</span>
                <span style={{ color: '#64748b', fontSize: '11px', display: 'block', marginTop: '2px' }}>Uploaded at registration desk</span>
              </div>
            </div>
            <div className="settle-modal-footer">
              <button type="button" className="settle-btn-reset" onClick={() => setDocModalData(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
          AFTER-BILL DISCOUNT MODAL
          ========================================================= */}
      {isDiscountModalOpen && discountBill && (
        <div className="settle-modal-backdrop" onClick={() => !discountSubmitting && setIsDiscountModalOpen(false)}>
          <div className="settle-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="settle-modal-header">
              <h3 style={{ color: '#fbbf24' }}>🏷️ Apply Authorized After-Bill Discount — {discountBill.labNo}</h3>
              <button
                type="button"
                className="settle-drawer-close"
                onClick={() => !discountSubmitting && setIsDiscountModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmitDiscount}>
              <div className="settle-modal-body">
                {/* Financial Overview */}
                <div className="settle-patient-banner" style={{ borderColor: 'rgba(251, 191, 36, 0.4)' }}>
                  <div>
                    <span style={{ color: '#94a3b8', fontSize: '11px', display: 'block' }}>Patient</span>
                    <strong>{discountBill.patientName}</strong>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      UHID: {discountBill.uhid}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                      Gross: ₹{(discountBill.grossAmount || 0).toFixed(2)} | Current Disc: ₹{(discountBill.discountAmount || 0).toFixed(2)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#38bdf8' }}>
                      Net: ₹{(discountBill.netAmount || 0).toFixed(2)} | Paid: ₹{(discountBill.paidAmount || 0).toFixed(2)}
                    </div>
                    <strong style={{ color: '#f87171', fontSize: '14px' }}>
                      Current Due: ₹{(discountBill.balanceDue || 0).toFixed(2)}
                    </strong>
                  </div>
                </div>

                {/* Discount Inputs */}
                <div className="settle-field">
                  <label className="settle-label">New Total Discount Amount (₹) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={discountBill.grossAmount}
                    className="settle-input"
                    value={newDiscountAmount}
                    onChange={(e) => setNewDiscountAmount(e.target.value)}
                    required
                  />
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                    Must be total discount against gross (₹{(discountBill.grossAmount || 0).toFixed(2)}).
                  </div>
                </div>

                {/* Recalculation Preview */}
                {(() => {
                  const gross = discountBill.grossAmount || 0;
                  const newDisc = parseFloat(newDiscountAmount) || 0;
                  const newNet = Math.max(0, gross - newDisc);
                  const paid = discountBill.paidAmount || 0;
                  const newDue = Math.max(0, newNet - paid);
                  return (
                    <div className="settle-balance-box">
                      <div>
                        <span>New Net: <strong style={{ color: '#38bdf8' }}>₹{newNet.toFixed(2)}</strong></span>
                        <span style={{ margin: '0 8px' }}>•</span>
                        <span>Paid: <strong>₹{paid.toFixed(2)}</strong></span>
                      </div>
                      <div>
                        <span>New Balance Due: </span>
                        <strong style={{ color: newDue > 0 ? '#f87171' : '#34d399', fontSize: '14px' }}>
                          ₹{newDue.toFixed(2)}
                        </strong>
                      </div>
                    </div>
                  );
                })()}

                <div className="settle-field">
                  <label className="settle-label">Approval Justification / Reason *</label>
                  <textarea
                    className="settle-input"
                    rows="2"
                    placeholder="Provide mandatory business reason for after-bill concession..."
                    value={discountReason}
                    onChange={(e) => setDiscountReason(e.target.value)}
                    required
                  />
                </div>

                <div className="settle-field">
                  <label className="settle-label">Authorizing Authority / Approver Name</label>
                  <input
                    type="text"
                    className="settle-input"
                    placeholder="e.g. Lab Director / Managing Director"
                    value={discountApproverName}
                    onChange={(e) => setDiscountApproverName(e.target.value)}
                  />
                </div>

                <div style={{ background: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.3)', borderRadius: '8px', padding: '10px', fontSize: '11px', color: '#fde68a' }}>
                  ℹ️ Notice: Changes will be permanently recorded in <code>f_discountafterbill</code> with user audit trail.
                </div>
              </div>

              <div className="settle-modal-footer">
                <button
                  type="button"
                  className="settle-btn-reset"
                  onClick={() => setIsDiscountModalOpen(false)}
                  disabled={discountSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="settle-btn-submit"
                  style={{ background: '#d97706' }}
                  disabled={discountSubmitting}
                >
                  {discountSubmitting ? 'Applying Discount...' : 'Authorize & Apply Discount'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =========================================================
          REFUND / CANCELLATION MODAL
          ========================================================= */}
      {isRefundModalOpen && refundBill && (
        <div className="settle-modal-backdrop" onClick={() => !refundSubmitting && setIsRefundModalOpen(false)}>
          <div className="settle-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="settle-modal-header">
              <h3 style={{ color: '#f87171' }}>↩️ Process Customer Refund — {refundBill.labNo}</h3>
              <button
                type="button"
                className="settle-drawer-close"
                onClick={() => !refundSubmitting && setIsRefundModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmitRefund}>
              <div className="settle-modal-body">
                <div className="settle-patient-banner" style={{ borderColor: 'rgba(239, 68, 68, 0.4)' }}>
                  <div>
                    <span style={{ color: '#94a3b8', fontSize: '11px', display: 'block' }}>Patient</span>
                    <strong>{refundBill.patientName}</strong>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      UHID: {refundBill.uhid}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ color: '#94a3b8', fontSize: '11px', display: 'block' }}>Total Paid Amount</span>
                    <strong style={{ color: '#34d399', fontSize: '15px' }}>
                      ₹ {(refundBill.paidAmount || 0).toFixed(2)}
                    </strong>
                    <div style={{ fontSize: '10px', color: '#94a3b8' }}>
                      Max Refundable: ₹{(refundBill.paidAmount || 0).toFixed(2)}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="settle-field">
                    <label className="settle-label">Refund Amount (₹) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={refundBill.paidAmount}
                      className="settle-input"
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      required
                    />
                  </div>

                  <div className="settle-field">
                    <label className="settle-label">Refund Payout Mode *</label>
                    <select
                      className="settle-select"
                      value={refundMode}
                      onChange={(e) => setRefundMode(e.target.value)}
                    >
                      <option value="Cash">Cash Payout</option>
                      <option value="Paytm">UPI / Paytm Transfer</option>
                      <option value="Online">Online Bank Transfer (NEFT/RTGS)</option>
                      <option value="Cheque">Cheque Reversal</option>
                      <option value="Credit Card">Card Refund / Chargeback</option>
                    </select>
                  </div>
                </div>

                {refundMode !== 'Cash' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div className="settle-field">
                      <label className="settle-label">Bank Name</label>
                      <input
                        type="text"
                        className="settle-input"
                        placeholder="e.g. HDFC / SBI"
                        value={refundBankName}
                        onChange={(e) => setRefundBankName(e.target.value)}
                      />
                    </div>
                    <div className="settle-field">
                      <label className="settle-label">Ref / Cheque / Txn ID</label>
                      <input
                        type="text"
                        className="settle-input"
                        placeholder="Reversal Reference No"
                        value={refundTransactionId || refundCardNo}
                        onChange={(e) => {
                          setRefundTransactionId(e.target.value);
                          setRefundCardNo(e.target.value);
                        }}
                      />
                    </div>
                  </div>
                )}

                <div className="settle-field">
                  <label className="settle-label">Refund / Cancellation Reason *</label>
                  <textarea
                    className="settle-input"
                    rows="3"
                    placeholder="Specify why this refund or cancellation is being authorized..."
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    required
                  />
                </div>

                <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '8px', padding: '10px', fontSize: '11px', color: '#fca5a5' }}>
                  ⚠️ Notice: Processing a refund generates an official negative credit voucher in <code>f_receipt</code> and atomically reduces collected balance in the financial ledger.
                </div>
              </div>

              <div className="settle-modal-footer">
                <button
                  type="button"
                  className="settle-btn-reset"
                  onClick={() => setIsRefundModalOpen(false)}
                  disabled={refundSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="settle-btn-submit"
                  style={{ background: '#dc2626' }}
                  disabled={refundSubmitting}
                >
                  {refundSubmitting ? 'Processing Refund...' : 'Authorize & Issue Refund Voucher'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =========================================================
          RECEIPT EDIT MODAL (12-HOUR LEGACY TIMEOUT RULE)
          ========================================================= */}
      {isReceiptEditModalOpen && editingReceipt && (
        <div className="settle-modal-backdrop" onClick={() => !editSubmitting && setIsReceiptEditModalOpen(false)}>
          <div className="settle-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="settle-modal-header">
              <h3 style={{ color: '#38bdf8' }}>✏️ Edit Receipt #{editingReceipt.receiptNo}</h3>
              <button
                type="button"
                className="settle-drawer-close"
                onClick={() => !editSubmitting && setIsReceiptEditModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmitReceiptEdit}>
              <div className="settle-modal-body">
                <div className="settle-patient-banner" style={{ borderColor: 'rgba(56, 189, 248, 0.4)' }}>
                  <div>
                    <span style={{ color: '#94a3b8', fontSize: '11px', display: 'block' }}>Receipt Details</span>
                    <strong style={{ color: '#38bdf8' }}>{editingReceipt.receiptNo}</strong>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      Date: {new Date(editingReceipt.paymentDate).toLocaleString()}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ color: '#94a3b8', fontSize: '11px', display: 'block' }}>Receipt Amount</span>
                    <strong style={{ color: '#34d399', fontSize: '16px' }}>
                      ₹ {(editingReceipt.amount || 0).toFixed(2)}
                    </strong>
                    <div style={{ fontSize: '10px', color: '#fbbf24' }}>
                      ⏰ Time Limit: 12h (Cashier) / 7d (Admin)
                    </div>
                  </div>
                </div>

                <div className="settle-field">
                  <label className="settle-label">Payment Mode *</label>
                  <select
                    className="settle-select"
                    value={editPaymentMode}
                    onChange={(e) => setEditPaymentMode(e.target.value)}
                    required
                  >
                    <option value="Cash">Cash Counter</option>
                    <option value="Paytm">Paytm / UPI QR</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Debit Card">Debit Card</option>
                    <option value="Online">Online NetBanking</option>
                    <option value="Cheque">Cheque</option>
                  </select>
                </div>

                {editPaymentMode !== 'Cash' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div className="settle-field">
                      <label className="settle-label">Bank Name</label>
                      <input
                        type="text"
                        className="settle-input"
                        placeholder="e.g. HDFC, SBI"
                        value={editBankName}
                        onChange={(e) => setEditBankName(e.target.value)}
                      />
                    </div>
                    <div className="settle-field">
                      <label className="settle-label">Card / Txn / Cheque No</label>
                      <input
                        type="text"
                        className="settle-input"
                        placeholder="Reference Number"
                        value={editTransactionId || editCardNo}
                        onChange={(e) => {
                          setEditTransactionId(e.target.value);
                          setEditCardNo(e.target.value);
                        }}
                      />
                    </div>
                  </div>
                )}

                <div className="settle-field">
                  <label className="settle-label">Updated Narration</label>
                  <input
                    type="text"
                    className="settle-input"
                    placeholder="e.g. Mode correction requested by patient"
                    value={editNarration}
                    onChange={(e) => setEditNarration(e.target.value)}
                  />
                </div>

                <div className="settle-field">
                  <label className="settle-label">Edit Reason (Mandatory Audit Requirement) *</label>
                  <textarea
                    className="settle-input"
                    rows="2"
                    placeholder="Specify why this receipt is being modified..."
                    value={editReason}
                    onChange={(e) => setEditReason(e.target.value)}
                    required
                  />
                </div>

                <div style={{ background: 'rgba(56, 189, 248, 0.1)', border: '1px solid rgba(56, 189, 248, 0.3)', borderRadius: '8px', padding: '10px', fontSize: '11px', color: '#bae6fd' }}>
                  ℹ️ All corrections are immutably logged to <code>f_receipt_edit_log</code> with old and new values, user ID, and timestamp.
                </div>
              </div>

              <div className="settle-modal-footer">
                <button
                  type="button"
                  className="settle-btn-reset"
                  onClick={() => setIsReceiptEditModalOpen(false)}
                  disabled={editSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="settle-btn-submit"
                  disabled={editSubmitting}
                >
                  {editSubmitting ? 'Saving Edits...' : 'Save Receipt Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Official A4 Laser Receipt Modal */}
      <ReceiptModal
        isOpen={isReceiptModalOpen}
        onClose={() => setIsReceiptModalOpen(false)}
        receipt={receiptData}
      />

      {/* ─────────────────────────────────────────────────────────────
          CANCEL TEST & REFUND MODAL
          ───────────────────────────────────────────────────────────── */}
      {isCancelModalOpen && (
        <div
          className="settle-modal-backdrop"
          onClick={() => {
            // Guard against accidental backdrop close if user has active selections
            if (selectedCancelTestIds.size === 0 && !cancelSubmitting) {
              setIsCancelModalOpen(false);
            }
          }}
        >
          <div
            className="settle-modal-card settle-modal-cancel-card"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header: Fixed & Non-Scrolling */}
            <div className="settle-modal-header settle-modal-header-cancel">
              <div>
                <h2 className="settle-modal-title">
                  <span style={{ color: '#e11d48' }}>✕</span> Cancel Test &amp; Refund Management
                </h2>
                <p className="settle-modal-subtitle">
                  Select tests to cancel with real-time financial recalculation, excess payment refund, and full audit logging.
                </p>
              </div>
              <button
                type="button"
                className="settle-modal-close"
                onClick={() => !cancelSubmitting && setIsCancelModalOpen(false)}
                disabled={cancelSubmitting}
                title="Close Modal"
              >
                ✕
              </button>
            </div>

            {cancelLoading ? (
              <div style={{ textAlign: 'center', padding: '48px 0', background: '#ffffff' }}>
                <div className="settle-spinner" style={{ margin: '0 auto 12px' }}></div>
                <p style={{ color: '#64748b', fontSize: '13px' }}>Evaluating clinical lifecycle and cancellation eligibility...</p>
              </div>
            ) : !cancelEligibility ? (
              <div style={{ padding: '36px', textAlign: 'center', background: '#ffffff' }}>
                <p style={{ color: '#ef4444', fontWeight: 600 }}>Unable to load order details for cancellation.</p>
                <button
                  type="button"
                  className="settle-btn-reset"
                  style={{ marginTop: '12px' }}
                  onClick={() => setIsCancelModalOpen(false)}
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleConfirmCancelTests} className="settle-modal-form-wrap">
                <div className="settle-modal-body-cancel">
                  {(() => {
                    const selectedTests = cancelEligibility.tests.filter((t) => selectedCancelTestIds.has(t.testId));
                    const selectedNet = selectedTests.reduce((sum, t) => sum + (t.netAmount || 0), 0);
                    const newNet = Math.max(0, cancelEligibility.netAmount - selectedNet);
                    const eligibleRefund = Math.max(0, cancelEligibility.paidAmount - newNet);
                    const newPaid = cancelEligibility.paidAmount - eligibleRefund;
                    const newDue = Math.max(0, newNet - newPaid);
                    const dueReduction = Math.min(selectedNet, cancelEligibility.dueAmount);
                    const eligibleTestsCount = cancelEligibility.tests.filter((t) => t.canCancel && !t.isCancel).length;
                    const effectiveReason = cancelReason === 'OTHER' ? customCancelReason.trim() : cancelReason.trim();
                    const needsApproval = !cancelEligibility.callerIsApprover && selectedTests.some((t) => t.callerCanDirectCancel === false);

                    return (
                      <>
                        {cancelConfirmedStep ? (
                          /* ─────────────────────────────────────────────────────────────
                             STEP 2: CONFIRMATION & AUDIT REVIEW VIEW
                             ───────────────────────────────────────────────────────────── */
                          <div className="settle-cancel-confirm-review">
                            {needsApproval && (
                              <div className="sc-authority-banner" style={{ marginBottom: '12px' }}>
                                🔒 One or more selected tests are beyond your cancellation authority for this centre.
                                Confirming below will <strong>not</strong> cancel anything yet — it raises an approval
                                request instead, and nothing changes on this bill until it is approved.
                              </div>
                            )}
                            <div className="sc-confirm-banner">
                              <span className="sc-confirm-banner-icon">⚠️</span>
                              <div className="sc-confirm-banner-text">
                                <h4 className="sc-confirm-banner-title">
                                  Review &amp; Confirm Test Cancellation
                                </h4>
                                <p className="sc-confirm-banner-desc">
                                  You are about to cancel <strong>{selectedCancelTestIds.size} test(s)</strong> for patient <strong>{cancelEligibility.patientName}</strong>.
                                  This will permanently remove the items from active laboratory processing benches, adjust customer billing ledgers, and log audit trail records.
                                </p>
                              </div>
                            </div>

                            <div className="sc-confirm-grid">
                              {/* Patient & Work Order Summary */}
                              <div className="sc-confirm-card">
                                <div className="sc-confirm-card-title">Patient &amp; Work Order Summary</div>
                                <div className="sc-confirm-row">
                                  <span className="sc-confirm-lbl">Patient Name:</span>
                                  <span className="sc-confirm-val">{cancelEligibility.patientName} ({cancelEligibility.ageGender})</span>
                                </div>
                                <div className="sc-confirm-row">
                                  <span className="sc-confirm-lbl">Patient UHID:</span>
                                  <span className="sc-confirm-val font-mono">{cancelEligibility.patientId}</span>
                                </div>
                                <div className="sc-confirm-row">
                                  <span className="sc-confirm-lbl">Work Order / Lab No:</span>
                                  <span className="sc-confirm-val font-mono">{cancelEligibility.ledgerTransactionNo}</span>
                                </div>
                                <div className="sc-confirm-row">
                                  <span className="sc-confirm-lbl">Diagnostic Centre:</span>
                                  <span className="sc-confirm-val">{cancelEligibility.centreName}</span>
                                </div>
                                <div className="sc-confirm-row">
                                  <span className="sc-confirm-lbl">Cancellation Reason:</span>
                                  <span className="sc-confirm-val" style={{ color: '#be123c', fontWeight: 700 }}>
                                    {effectiveReason || 'Not specified'}
                                  </span>
                                </div>
                                {cancelRemarks && (
                                  <div className="sc-confirm-row">
                                    <span className="sc-confirm-lbl">Clinical Notes:</span>
                                    <span className="sc-confirm-val">{cancelRemarks}</span>
                                  </div>
                                )}
                              </div>

                              {/* Financial Audit Recalculation */}
                              <div className="sc-confirm-card">
                                <div className="sc-confirm-card-title">Financial Audit Recalculation</div>
                                <div className="sc-confirm-row">
                                  <span className="sc-confirm-lbl">Original Net Bill:</span>
                                  <span className="sc-confirm-val">₹{cancelEligibility.netAmount.toFixed(2)}</span>
                                </div>
                                <div className="sc-confirm-row">
                                  <span className="sc-confirm-lbl">Cancelled Test Value:</span>
                                  <span className="sc-confirm-val" style={{ color: '#e11d48', fontWeight: 700 }}>
                                    -₹{selectedNet.toFixed(2)}
                                  </span>
                                </div>
                                <div className="sc-confirm-row">
                                  <span className="sc-confirm-lbl">Revised Net Bill:</span>
                                  <span className="sc-confirm-val" style={{ fontWeight: 800 }}>
                                    ₹{newNet.toFixed(2)}
                                  </span>
                                </div>
                                <div className="sc-confirm-row">
                                  <span className="sc-confirm-lbl">Total Amount Collected:</span>
                                  <span className="sc-confirm-val">₹{cancelEligibility.paidAmount.toFixed(2)}</span>
                                </div>
                                <div className="sc-confirm-row sc-confirm-row-highlight">
                                  <span className="sc-confirm-lbl">Excess Refund to Patient:</span>
                                  <span className="sc-confirm-val" style={{ color: '#047857', fontWeight: 800, fontSize: '13px' }}>
                                    ₹{eligibleRefund.toFixed(2)}
                                  </span>
                                </div>
                                {eligibleRefund > 0.005 && (
                                  <div className="sc-confirm-row">
                                    <span className="sc-confirm-lbl">Refund Payment Mode:</span>
                                    <span className="sc-confirm-val font-semibold">
                                      {cancelRefundMode} {cancelBankName ? `(${cancelBankName})` : ''} {cancelTransactionId || cancelCardNo ? `• Ref: ${cancelTransactionId || cancelCardNo}` : ''}
                                    </span>
                                  </div>
                                )}
                                <div className="sc-confirm-row">
                                  <span className="sc-confirm-lbl">Revised Outstanding Due:</span>
                                  <span className="sc-confirm-val" style={{ color: newDue > 0 ? '#b45309' : '#047857', fontWeight: 800 }}>
                                    ₹{newDue.toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Tests Slated for Cancellation */}
                            <div className="sc-confirm-tests-box">
                              <div className="sc-confirm-tests-title">
                                Selected Tests for Immediate Cancellation ({selectedTests.length}):
                              </div>
                              <div className="sc-confirm-tests-list">
                                {selectedTests.map((t) => (
                                  <div key={t.testId} className="sc-confirm-test-item">
                                    <div className="sc-confirm-test-name">
                                      <strong>{t.itemName}</strong>
                                      <span className="sc-confirm-test-code">{t.itemCode} • {t.departmentName || 'General'}</span>
                                    </div>
                                    <div className="sc-confirm-test-rate">
                                      -₹{t.netAmount.toFixed(2)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* ─────────────────────────────────────────────────────────────
                             STEP 1: TEST SELECTION & FINANCIAL IMPACT VIEW
                             ───────────────────────────────────────────────────────────── */
                          <>
                            {/* 1. Order & Patient Header Strip */}
                            <div className="settle-cancel-order-strip">
                              <div className="sc-strip-col">
                                <span className="sc-strip-label">Patient</span>
                                <strong className="sc-strip-val">{cancelEligibility.patientName}</strong>
                                <span className="sc-strip-sub">{cancelEligibility.ageGender} • UHID: {cancelEligibility.patientId}</span>
                              </div>
                              <div className="sc-strip-col">
                                <span className="sc-strip-label">Work Order / Lab No</span>
                                <strong className="sc-strip-val font-mono">{cancelEligibility.ledgerTransactionNo}</strong>
                                <span className="sc-strip-sub">Centre: {cancelEligibility.centreName}</span>
                              </div>
                              <div className="sc-strip-col sc-strip-fin">
                                <div className="sc-fin-chip">
                                  <span>Net Bill</span>
                                  <strong>₹{cancelEligibility.netAmount.toFixed(2)}</strong>
                                </div>
                                <div className="sc-fin-chip">
                                  <span>Paid</span>
                                  <strong>₹{cancelEligibility.paidAmount.toFixed(2)}</strong>
                                </div>
                                <div className="sc-fin-chip" style={{ color: cancelEligibility.dueAmount > 0 ? '#b45309' : '#047857' }}>
                                  <span>Due</span>
                                  <strong>₹{cancelEligibility.dueAmount.toFixed(2)}</strong>
                                </div>
                              </div>
                            </div>

                            {/* 1b. Your cancellation authority for this centre (centre_cancellation_policy) */}
                            <div className={`sc-authority-banner ${cancelEligibility.callerIsApprover ? 'is-approver' : ''}`}>
                              {cancelEligibility.callerIsApprover ? (
                                <span>🔓 You are this centre's cancellation approver — you can cancel any eligible test directly.</span>
                              ) : (
                                <span>
                                  🔒 Your authority covers direct cancellation up to{' '}
                                  <strong>
                                    {CANCELLATION_STAGE_LABELS[cancelEligibility.effectiveMaxSelfServiceStage] || 'Approval'}
                                  </strong>
                                  . Tests beyond that will be sent as an approval request instead of being cancelled immediately.
                                </span>
                              )}
                            </div>

                            {/* 2. Toolbar: Search Filter + Selection Count + Select All Eligible */}
                            <div className="sc-toolbar-row">
                              <div className="sc-search-wrapper">
                                <span className="sc-search-icon">🔍</span>
                                <input
                                  type="text"
                                  className="sc-search-input"
                                  placeholder="Search test by name, code, or department..."
                                  value={cancelTestSearch}
                                  onChange={(e) => setCancelTestSearch(e.target.value)}
                                />
                                {cancelTestSearch && (
                                  <button
                                    type="button"
                                    className="sc-search-clear"
                                    onClick={() => setCancelTestSearch('')}
                                    title="Clear search"
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                              <div className="sc-selection-meta">
                                <span className="sc-selection-badge">
                                  <strong>{selectedCancelTestIds.size}</strong> of {eligibleTestsCount} Eligible Tests Selected
                                </span>
                                <button
                                  type="button"
                                  className="settle-btn-select-all"
                                  onClick={handleSelectAllEligibleTests}
                                >
                                  {selectedCancelTestIds.size === eligibleTestsCount && eligibleTestsCount > 0
                                    ? 'Deselect All'
                                    : 'Select All Eligible'}
                                </button>
                              </div>
                            </div>

                            {/* 3. Test Line Items Table with Fixed Height & Scrollbar */}
                            <div className="settle-cancel-tests-table-wrap">
                              <table className="settle-cancel-table">
                                <colgroup>
                                  <col className="sc-col-select" />
                                  <col className="sc-col-name" />
                                  <col className="sc-col-dept" />
                                  <col className="sc-col-rate" />
                                  <col className="sc-col-net" />
                                  <col className="sc-col-stage" />
                                  <col className="sc-col-eligibility" />
                                </colgroup>
                                <thead>
                                  <tr>
                                    <th style={{ textAlign: 'center' }}>Select</th>
                                    <th>Test Code &amp; Name</th>
                                    <th>Department</th>
                                    <th style={{ textAlign: 'right' }}>Rate (₹)</th>
                                    <th style={{ textAlign: 'right' }}>Net (₹)</th>
                                    <th>Clinical Stage</th>
                                    <th>Eligibility</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {displayedCancelTests.length === 0 ? (
                                    <tr>
                                      <td colSpan="7" style={{ textAlign: 'center', padding: '28px', color: '#64748b' }}>
                                        {cancelTestSearch ? (
                                          <>
                                            No tests matching "<strong>{cancelTestSearch}</strong>"
                                            <div style={{ marginTop: '8px' }}>
                                              <button
                                                type="button"
                                                className="settle-btn-select-all"
                                                onClick={() => setCancelTestSearch('')}
                                              >
                                                Clear Search Filter
                                              </button>
                                            </div>
                                          </>
                                        ) : (
                                          'No tests found on this order.'
                                        )}
                                      </td>
                                    </tr>
                                  ) : (
                                    displayedCancelTests.map((test) => {
                                      const isSelected = selectedCancelTestIds.has(test.testId);
                                      const isCancelled = test.isCancel || test.lifecycleStage === 'CANCELLED';
                                      const canSelect = test.canCancel && !isCancelled;

                                      return (
                                        <tr
                                          key={test.testId}
                                          className={`sc-row ${isSelected ? 'sc-row-selected' : ''} ${isCancelled ? 'sc-row-cancelled' : ''} ${!canSelect && !isCancelled ? 'sc-row-blocked' : ''}`}
                                        >
                                          <td style={{ textAlign: 'center' }}>
                                            <input
                                              type="checkbox"
                                              className="sc-checkbox"
                                              checked={isSelected}
                                              disabled={!canSelect}
                                              onChange={() => handleToggleCancelTest(test.testId)}
                                              aria-label={`Select ${test.itemName}`}
                                            />
                                          </td>
                                          <td>
                                            <div className="sc-test-name" style={{ textDecoration: isCancelled ? 'line-through' : 'none', color: isCancelled ? '#64748b' : '#0f172a' }}>
                                              {test.itemName}
                                            </div>
                                            <div className="sc-test-code">
                                              {test.itemCode} {test.barcodeNo ? `• 🏷️ ${test.barcodeNo}` : ''}
                                            </div>
                                            {test.warningMessage && !isCancelled && (
                                              <div className="sc-warning-note">
                                                ⚠️ {test.warningMessage}
                                              </div>
                                            )}
                                            {!test.canCancel && !isCancelled && test.blockingReason && (
                                              <span className="sc-blocked-note">
                                                ⛔ {test.blockingReason}
                                              </span>
                                            )}
                                          </td>
                                          <td style={{ color: '#475569', fontSize: '11.5px' }}>
                                            {test.departmentName || 'General'}
                                          </td>
                                          <td style={{ textAlign: 'right', fontSize: '12px', color: '#64748b' }}>
                                            ₹{test.rate.toFixed(2)}
                                          </td>
                                          <td style={{ textAlign: 'right', fontWeight: 700, fontSize: '12.5px', color: isCancelled ? '#94a3b8' : '#0284c7' }}>
                                            ₹{test.netAmount.toFixed(2)}
                                          </td>
                                          <td>
                                            <span className={`sc-stage-badge sc-stage-${(test.lifecycleStage || '').toLowerCase()}`}>
                                              {(test.lifecycleStage || 'PENDING').replace(/_/g, ' ')}
                                            </span>
                                          </td>
                                          <td>
                                            {isCancelled ? (
                                              <span className="sc-badge-cancelled">Already Cancelled</span>
                                            ) : test.canCancel ? (
                                              <span className={test.callerCanDirectCancel === false ? 'sc-badge-approval' : 'sc-badge-eligible'}>
                                                {test.callerCanDirectCancel === false ? '🔒 Needs Approval' : '✓ Direct Cancel'}
                                              </span>
                                            ) : (
                                              <span className="sc-badge-blocked" title={test.blockingReason || 'Cannot cancel at current stage'}>
                                                Prohibited
                                              </span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })
                                  )}
                                </tbody>
                              </table>
                            </div>

                            {/* 4. Real-time Financial Recalculation Preview */}
                            <div className="settle-cancel-calc-card">
                              <div className="sc-calc-header">
                                <span>🧮</span>
                                <strong>Real-Time Financial Impact Simulation</strong>
                              </div>
                              <div className="sc-calc-grid">
                                <div className="sc-calc-box box-cancelled">
                                  <span className="sc-calc-lbl">Cancelled Amount</span>
                                  <span className="sc-calc-val" style={{ color: '#e11d48' }}>
                                    -₹{selectedNet.toFixed(2)}
                                  </span>
                                  <span className="sc-calc-sub">{selectedTests.length} test(s) selected</span>
                                </div>

                                <div className="sc-calc-box box-net">
                                  <span className="sc-calc-lbl">New Net Bill</span>
                                  <span className="sc-calc-val">
                                    ₹{newNet.toFixed(2)}
                                  </span>
                                  <span className="sc-calc-sub">was ₹{cancelEligibility.netAmount.toFixed(2)}</span>
                                </div>

                                <div className="sc-calc-box highlight-refund">
                                  <span className="sc-calc-lbl">Eligible Cash/Online Refund</span>
                                  <span className="sc-calc-val" style={{ color: '#047857' }}>
                                    ₹{eligibleRefund.toFixed(2)}
                                  </span>
                                  <span className="sc-calc-sub">
                                    {eligibleRefund > 0 ? 'Excess payment returned to patient' : 'No cash refund required'}
                                  </span>
                                </div>

                                <div className="sc-calc-box box-due">
                                  <span className="sc-calc-lbl">Bill Due Reduction</span>
                                  <span className="sc-calc-val" style={{ color: '#0284c7' }}>
                                    ₹{dueReduction.toFixed(2)}
                                  </span>
                                  <span className="sc-calc-sub">New Due: ₹{newDue.toFixed(2)}</span>
                                </div>
                              </div>
                            </div>

                            {/* 5. Form Inputs: Reason & Remarks */}
                            <div className="settle-form-row">
                              <div className="settle-field" style={{ flex: 1 }}>
                                <label className="settle-label">Cancellation Reason <span style={{ color: '#ef4444' }}>*</span></label>
                                <select
                                  className="settle-input"
                                  value={cancelReason}
                                  onChange={(e) => setCancelReason(e.target.value)}
                                  required
                                >
                                  {cancelEligibility.reasons.map((r) => (
                                    <option key={r.reasonCode} value={r.reasonName}>
                                      {r.reasonName}
                                    </option>
                                  ))}
                                  <option value="OTHER">Other / Custom Reason</option>
                                </select>
                              </div>

                              {cancelReason === 'OTHER' && (
                                <div className="settle-field" style={{ flex: 1 }}>
                                  <label className="settle-label">Specify Custom Reason <span style={{ color: '#ef4444' }}>*</span></label>
                                  <input
                                    type="text"
                                    className="settle-input"
                                    placeholder="State clinical / administrative justification..."
                                    value={customCancelReason}
                                    onChange={(e) => setCustomCancelReason(e.target.value)}
                                    required
                                  />
                                </div>
                              )}
                            </div>

                            <div className="settle-field">
                              <label className="settle-label">Additional Clinical / Cancellation Notes (Optional)</label>
                              <textarea
                                className="settle-input"
                                rows={2}
                                placeholder="e.g. Patient requested cancellation before phlebotomy; approved by Dr. Sharma..."
                                value={cancelRemarks}
                                onChange={(e) => setCancelRemarks(e.target.value)}
                              />
                            </div>

                            {/* 6. Refund Payment Details (if eligible refund > 0) */}
                            {eligibleRefund > 0.005 && (
                              <div className="settle-cancel-refund-section">
                                <h4 className="sc-refund-title">
                                  <span>💵</span> Refund Disbursement Details (₹{eligibleRefund.toFixed(2)})
                                </h4>

                                <div className="settle-form-row">
                                  <div className="settle-field" style={{ flex: 1 }}>
                                    <label className="settle-label">Refund Payment Mode</label>
                                    <select
                                      className="settle-input"
                                      value={cancelRefundMode}
                                      onChange={(e) => setCancelRefundMode(e.target.value)}
                                    >
                                      <option value="Cash">Cash</option>
                                      <option value="UPI">UPI / QR</option>
                                      <option value="Card">Credit/Debit Card</option>
                                      <option value="NetBanking">Net Banking</option>
                                      <option value="Cheque">Cheque</option>
                                    </select>
                                  </div>

                                  {cancelRefundMode !== 'Cash' && (
                                    <>
                                      <div className="settle-field" style={{ flex: 1 }}>
                                        <label className="settle-label">Bank Name</label>
                                        <input
                                          type="text"
                                          className="settle-input"
                                          placeholder="e.g. HDFC / SBI"
                                          value={cancelBankName}
                                          onChange={(e) => setCancelBankName(e.target.value)}
                                        />
                                      </div>
                                      <div className="settle-field" style={{ flex: 1 }}>
                                        <label className="settle-label">Txn / Card Ref No</label>
                                        <input
                                          type="text"
                                          className="settle-input"
                                          placeholder="UPI Ref / Last 4 digits"
                                          value={cancelTransactionId || cancelCardNo}
                                          onChange={(e) => {
                                            setCancelTransactionId(e.target.value);
                                            setCancelCardNo(e.target.value);
                                          }}
                                        />
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Footer: Fixed & Stable */}
                <div className="settle-modal-footer-cancel">
                  <button
                    type="button"
                    className="settle-btn-reset"
                    onClick={() => {
                      if (cancelConfirmedStep) {
                        setCancelConfirmedStep(false);
                      } else {
                        setIsCancelModalOpen(false);
                      }
                    }}
                    disabled={cancelSubmitting}
                  >
                    {cancelConfirmedStep ? '← Back to Selection' : 'Close / Discard'}
                  </button>

                  <button
                    type="submit"
                    className="settle-btn-submit"
                    disabled={cancelSubmitting || selectedCancelTestIds.size === 0}
                    style={{
                      background: cancelConfirmedStep
                        ? 'linear-gradient(135deg, #be123c, #9f1239)'
                        : 'linear-gradient(135deg, #e11d48, #be123c)',
                      borderColor: '#be123c',
                      color: '#ffffff',
                      fontWeight: 700,
                    }}
                  >
                    {cancelSubmitting
                      ? 'Cancelling & Adjusting Ledgers...'
                      : cancelConfirmedStep
                      ? '⛔ Confirm & Execute Cancellation'
                      : `Review Cancellation (${selectedCancelTestIds.size} Tests) →`}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}


    </div>
  );
};

export default SettlementPage;
